/**
 * Formularul de gravura — comportamentul.
 *
 * Markup-ul e in `snippets/gravura-form.liquid`, stilul in `gravura.css`. Aici sta doar ce se
 * intampla: campurile se construiesc din reteta produsului, textul tastat se aseaza pe piesa
 * reala, fonturile care nu pot scrie ce s-a tastat se sting, iar in cos ajung proprietatile
 * emise de server, nu unele compuse aici.
 *
 * Trei lucruri de stiut inainte sa umbli:
 *
 *   1. Niciun control vizibil n-are `name`. In /cart/add.js pleaca DOAR inputurile ascunse pe
 *      care le scrie `/gravura/valideaza`, toate invelite in `properties[…]`. De aceea `form.id`
 *      ramane inputul variantei, si de aceea nu se poate strecura in cos text neverificat.
 *   2. Starea NU sta in DOM. La schimbarea de produs tema inlocuieste tot <product-rerender>, cu
 *      elementul nostru cu tot; ce s-a tastat traieste in `STARE`, pe handle.
 *   3. Ascultatorul de submit se pune pe <product-form>, in faza de capturare. Pe formularul
 *      insusi n-ar ajuta: la tinta, ascultatorii merg in ordinea inregistrarii, iar
 *      <product-form> se inregistreaza inaintea noastra.
 *
 * Un singur fisier, desi e lung: adresa unui asset fara `?v=` — singura la care ar putea ajunge
 * un `import './altceva.js'` — se serveste cu `cache-control: max-age=31557600`, deci o corectura
 * n-ar ajunge un an la cine a vizitat magazinul o data.
 */

const AMANARE_MS = 250;
const AMANARE_VALIDARE_MS = 900; // dupa ultima tasta, inainte sa se scrie proprietatile
const MAX_CARACTERE = 400; // plafon dur, nu limita reala: aceea vine per fata, de la server
const POZA_MAX_OCTETI = 12 * 1024 * 1024;

/*
 * INCADRAREA POZEI (piesele „cu ochi").
 *
 * Omul incarca o fotografie intreaga, dar pe piesa se graveaza doar OCHII. Pana
 * pe 9 sep 2026 bucata se taia prin mijlocul geometric al pozei, iar clientul
 * vedea in macheta o fata intreaga si credea ca asa ii gravam bijuteria.
 *
 * Acum bucata o alege el: un chenar cu FORMA zonei gravate (36 x 15 mm → 2,4:1),
 * peste care trage poza si o mareste. Ce se vede in chenar e literalmente ce
 * intra in zona. Chenarul se asaza SINGUR pe ochi cand ii gaseste (face-api,
 * incarcat abia dupa ce omul a ales poza), si ramane al omului: orice mutare a
 * lui bate detectia. Alegerea pleaca in validare ca `poza_crop`, intra semnata
 * in jeton si ajunge pana pe foaia de atelier.
 */

/** Cea mai lunga latura a pozei incarcate, in pixeli. Ochii pe 36 mm nu cer mai mult. */
const POZA_LATURA_MAX = 2000;

/**
 * Chenarul pe ochi: cat de lat fata de distanta dintre pupile.
 *
 * La 2,3 intra amandoi ochii cu sprancenele si putin din tample — cat arata
 * pozele de produs. Mai putin taie coada ochiului, mai mult aduce nasul.
 */
const CADRU_PE_OCHI = 2.3;

/**
 * Detectia fetelor, doar in browser si doar la nevoie.
 *
 * Fork-ul intretinut al lui face-api (tfjs 4, merge pe Safari de pe iPhone):
 * 340 KB comprimat plus doua modele mici (~220 KB), de pe acelasi CDN, la
 * aceeasi versiune — nimic nu se schimba sub noi. Se incarca DUPA ce omul a ales
 * poza, nu la deschiderea paginii: cine nu incarca nimic nu plateste nimic.
 * Daca nu se incarca (retea, CDN blocat), chenarul ramane si se muta cu mana.
 */
const FACEAPI = Object.freeze({
  script: 'https://cdn.jsdelivr.net/npm/@vladmandic/face-api@1.7.15/dist/face-api.js',
  modele: 'https://cdn.jsdelivr.net/npm/@vladmandic/face-api@1.7.15/model',
});
let faceApiPromisa = null;

const incarcaFaceApi = () => {
  faceApiPromisa ??= new Promise((rezolva, respinge) => {
    if (window.faceapi) { rezolva(window.faceapi); return; }
    const script = document.createElement('script');
    script.src = FACEAPI.script;
    script.async = true;
    script.onload = () => (window.faceapi ? rezolva(window.faceapi) : respinge(new Error('face-api lipsa')));
    script.onerror = () => respinge(new Error('face-api nu s-a incarcat'));
    document.head.append(script);
  }).then(async (faceapi) => {
    await Promise.all([
      faceapi.nets.tinyFaceDetector.loadFromUri(FACEAPI.modele),
      faceapi.nets.faceLandmark68TinyNet.loadFromUri(FACEAPI.modele),
    ]);
    return faceapi;
  }).catch((eroare) => {
    // Data viitoare se incearca din nou — o retea cazuta o clipa nu inseamna „niciodata".
    faceApiPromisa = null;
    throw eroare;
  });
  return faceApiPromisa;
};

/**
 * Ochii din poza: centrul fiecaruia, in pixelii imaginii date. `null` cand nu
 * e nicio fata. Cu mai multe fete se ia cea mai MARE — de obicei cea din prim
 * plan — si se spune ca au fost mai multe, ca omul sa verifice.
 */
async function gasesteOchii(imagine) {
  const faceapi = await incarcaFaceApi();
  const optiuni = new faceapi.TinyFaceDetectorOptions({ inputSize: 416, scoreThreshold: 0.35 });
  const gasite = await faceapi.detectAllFaces(imagine, optiuni).withFaceLandmarks(true);
  if (!gasite?.length) return null;
  const mare = [...gasite].sort((a, b) => b.detection.box.area - a.detection.box.area)[0];
  const centru = (puncte) => ({
    x: puncte.reduce((s, p) => s + p.x, 0) / puncte.length,
    y: puncte.reduce((s, p) => s + p.y, 0) / puncte.length,
  });
  return {
    stanga: centru(mare.landmarks.getLeftEye()),
    dreapta: centru(mare.landmarks.getRightEye()),
    fete: gasite.length,
  };
}

/** Cat se poate roti poza, in grade. Un cap inclinat mai mult de atat e o alta poza. */
const ROTIRE_MAX = 45;

/**
 * Chenarul (pixeli de imagine) centrat pe ochi, in forma zonei, cu poza ROTITA
 * cat sa iasa linia ochilor orizontala — pe piesa ochii stau drept, oricum ar
 * fi tinut capul in poza. Unghiul e cel cu care se roteste poza in jurul
 * centrului chenarului (sensul acelor de ceasornic), ca in `incadrare.js`.
 */
function cadrulDinOchi(ochi, aspect, latime, inaltime) {
  const cx = (ochi.stanga.x + ochi.dreapta.x) / 2;
  const cy = (ochi.stanga.y + ochi.dreapta.y) / 2;
  const distanta = Math.hypot(ochi.stanga.x - ochi.dreapta.x, ochi.stanga.y - ochi.dreapta.y);
  // De la ochiul din stanga pozei spre cel din dreapta: unghiul liniei lor fata
  // de orizontala. Poza se roteste INVERS cu atat, ca linia sa cada pe orizontala.
  const [a, b] = ochi.stanga.x <= ochi.dreapta.x ? [ochi.stanga, ochi.dreapta] : [ochi.dreapta, ochi.stanga];
  const inclinare = (Math.atan2(b.y - a.y, b.x - a.x) * 180) / Math.PI;
  const unghi = Math.max(-ROTIRE_MAX, Math.min(ROTIRE_MAX, Math.round(-inclinare * 10) / 10));
  let w = Math.min(latime, Math.max(24, distanta * CADRU_PE_OCHI));
  let h = w / aspect;
  if (h > inaltime) { h = inaltime; w = h * aspect; }
  return { x: cx - w / 2, y: cy - h / 2, w, h, unghi };
}

/** O imagine incarcata dintr-o adresa, ca promisiune. */
const imagineDin = (adresa) => new Promise((rezolva, respinge) => {
  const img = new Image();
  img.onload = () => rezolva(img);
  img.onerror = () => respinge(new Error('poza nu se poate citi'));
  img.src = adresa;
});
const CONTUR_RAZA_MM = 2; // colturile piesei cand desenul nu spune altceva
const NS_SVG = 'http://www.w3.org/2000/svg';

/**
 * Grupele de alfabet, in aceeasi impartire ca `src/mockup/glyphs.js`.
 *
 * Se verifica in browser, la fiecare tasta: `/gravura/config` spune deja ce alfabete are fiecare
 * font, deci un drum la server pentru fiecare litera ar fi si lent, si inutil. Simbolurile (♥ ∞)
 * nu intra in nicio grupa fiindca le desenam noi, nu ies din font.
 */
const ALFABETE = [
  ['romana', /[ăâîșțĂÂÎȘȚşţŞŢ]/gu],
  ['chirilica', /[Ѐ-ӿ]/gu],
  ['latina', /[A-Za-z0-9]/gu],
];

const radacina = () => window.Shopify?.routes?.root ?? '/';
const mesajul = (problema) => (typeof problema === 'string' ? problema : problema?.mesaj ?? '');

/** Literele din text pe care fontul nu le poate scrie. Lista goala = fontul merge. */
function litereLipsa(font, text) {
  const are = new Set(font.alfabete ?? []);
  const lipsa = [];
  for (const [cod, tipar] of ALFABETE) {
    if (are.has(cod)) continue;
    const gasite = String(text ?? '').match(tipar);
    if (gasite) lipsa.push(...new Set(gasite));
  }
  return lipsa;
}

const elem = (nume, atribute) => {
  const nod = document.createElementNS(NS_SVG, nume);
  for (const [cheie, valoare] of Object.entries(atribute)) nod.setAttribute(cheie, valoare);
  return nod;
};

/**
 * O cale din raspuns, ca element.
 *
 * Contractul scrie `cai` ca siruri `d`, dar motorul tine fragmente `<path …/>` gata facute, cu
 * `transform` pe ele. Se accepta amandoua — si se citesc cu regex, nu cu `innerHTML`: markup
 * venit de la server, pus direct in pagina, e o usa deschisa care n-are de ce sa existe aici.
 */
function caleaDin(bucata) {
  if (typeof bucata !== 'string') return null;
  if (!bucata.trimStart().startsWith('<')) return elem('path', { d: bucata });
  const d = bucata.match(/\bd="([^"]*)"/);
  if (!d) return null;
  const transform = bucata.match(/\btransform="([^"]*)"/);
  const atribute = transform ? { d: d[1], transform: transform[1] } : { d: d[1] };
  // Simbolul din desen se poate APASA ca sa fie schimbat — marcajul vine de la
  // server, care singur stie ce glifa e simbol si ce glifa e litera.
  const simbol = bucata.match(/\bdata-simbol="([^"]*)"/);
  if (simbol) {
    atribute['data-simbol'] = simbol[1];
    atribute.class = 'gravura__simbol-desen';
  }
  return elem('path', atribute);
}

/**
 * E crucea o cruce, sau o zona dreptunghiulara numita asa?
 *
 * Aceeasi regula ca `eCruceReala` din `src/mockup/shapes.js`: jumatate din
 * sabloanele de cruce descriu BRATUL TRANSVERSAL (`28×7.4` cu bratul 27.5),
 * adica o bara masurata pe o bijuterie care e cruce. Un brat cat toata latimea
 * nu lasa loc de brate laterale — deci e dreptunghi, si asa se si deseneaza.
 */
function eCruce(zona, latimeZona = null, inaltimeZona = null) {
  if ((zona?.forma ?? null) !== 'cruce') return false;
  const latime = Number(latimeZona ?? zona.latimeMm);
  const inaltime = Number(inaltimeZona ?? zona.inaltimeMm);
  const brat = Number(zona.bratMm ?? 9);
  return brat < latime - 0.6 && inaltime > brat + 0.6;
}

/** Textul sta pe curbura? Sus (`arc`) sau jos (`arc_jos`) — doar pe cerc. */
const peCurbura = (zona) => zona?.forma === 'cerc' && (zona.mod === 'arc' || zona.mod === 'arc_jos');

/** Dreptunghi (bara/tag) cu textul rotit pe latura lunga. */
const eVertical = (zona) => (zona?.forma === 'bara' || zona?.forma === 'tag') && zona.mod === 'vertical';

/**
 * Arcul pe care se scrie — aceleasi cifre ca `arcArea` din `src/mockup/shapes.js`.
 *
 * Pe un disc plin textul sta pe arcul de sus, cu litera cel mult 42 % din raza. Pe
 * un INEL (`bandaMm`) litera nu poate fi mai inalta decat banda, iar linia de baza
 * se lipeste de marginea benzii: de cea dinspre gaura la `arc`, de cea exterioara
 * la `arc_jos`, unde literele stau drepte pe arcul de jos, ca un zambet.
 */
function arcUtila(zona, marja) {
  const R = Math.min(Number(zona.latimeMm), Number(zona.inaltimeMm)) / 2;
  const raza = R - marja;
  const jos = zona.mod === 'arc_jos';
  const banda = Number(zona.bandaMm) > 0 ? Math.min(Number(zona.bandaMm), R) : null;
  if (!banda && !jos) {
    return { latimeMm: 2 * Math.PI * raza * 0.55, inaltimeMm: raza * 0.42, arc: { razaMm: raza * 0.78, jos: false } };
  }
  const inaltime = Math.max(0.5, banda ? banda - marja * 2 : raza * 0.42);
  const razaBaza = banda
    ? (jos ? R - marja - inaltime * 0.2 : R - banda + marja + inaltime * 0.2)
    : raza - inaltime * 0.2;
  return {
    latimeMm: 2 * Math.PI * Math.max(0.5, razaBaza) * 0.55,
    inaltimeMm: inaltime,
    arc: { razaMm: Math.max(0.5, razaBaza), jos },
  };
}

/**
 * Cum se rup randurile: `auto` — motorul (`src/mockup/randuri.js`) alege ruperea
 * cu litera cea mai mare si o ingheata la validare. Singura valoare trimisa.
 */
const ASEZARE_IMPLICITA = 'auto';

/**
 * Sub ce inaltime de litera i se spune clientului sa scurteze textul. Peste ea,
 * semnul e „Gravura buna", fara trepte si fara milimetri (cerut de Vlad, 26 sep 2026).
 */
const LITERA_MINIMA_LINISTITA_MM = 2;

/** Peste cate semne un text e „chiar lung" — o data si jumatate pragul de 16. */
const TEXT_CHIAR_LUNG = 24;

/** Clipurile ghidului audio, pe server, dupa pasul pe care il explica. */
const FISIERE_GHID = { text: 1, simbol: 2, font: 3, impachetare: 4, macheta: 5, final: 6 };

/**
 * Metalele piesei, din titlul variantei („Auriu", „Gold & Silver", „Roz"), in
 * ORDINEA in care sunt scrise.
 *
 * Previzualizarea arata piesa in metalul ei, nu o silueta: pe aur gravura iese
 * bronz inchis, pe argint gri-grafit, pe negru ALBA — exact ce vede clientul
 * cand primeste bijuteria. Pe o varianta bicolora primul metal e FATA, al
 * doilea SPATELE: „Gold & Black" e o cruce aurie pe fata si neagra pe spate,
 * iar gravura de pe spate iese alba pe negru (confirmat de Vlad, 26 sep 2026).
 */
const METALE = [
  ['aur', /aur|gold|galben|золот/i],
  ['roz', /roz|rose|розов/i],
  ['argint', /argint|silver|otel|oțel|steel|inox|сереб|стал/i],
  ['negru', /negr|black|черн/i],
];

function metaleDin(titlu) {
  const text = String(titlu ?? '');
  return METALE
    .map(([metal, tipar]) => ({ metal, poz: text.search(tipar) }))
    .filter((gasit) => gasit.poz >= 0)
    .sort((a, b) => a.poz - b.poz)
    .map((gasit) => gasit.metal);
}

/** Pe ce metal cade o fata: fata = primul metal, orice alta fata (spate, interior) = ultimul. */
const metalulPartii = (metale, parte) => (parte === 'fata' ? metale[0] : metale[metale.length - 1]) ?? null;

/**
 * Directia fibrei periate: de-a lungul laturii LUNGI a piesei, ca pe metalul
 * real (o cruce periata are fibra verticala, o placuta de bratara orizontala).
 */
const fibraPiesei = (latime, inaltime) => (inaltime > latime ? 'v' : 'o');

/**
 * Gradientii metalelor si filtrul de gravura, o singura data pe pagina.
 *
 * Stau intr-un `<svg>` ascuns, separat de desene: clonele din vitrina si din
 * fereastra marita trebuie sa-i gaseasca si ele, iar un `id` repetat in fiecare
 * desen ar fi un document invalid. CSS-ul ii cere cu `url(#mlx-metal-aur)`.
 */
function asiguraDefinitiile() {
  if (document.getElementById('mlx-gravura-defs')) return;
  const svg = elem('svg', {
    id: 'mlx-gravura-defs', width: 0, height: 0, 'aria-hidden': 'true', focusable: 'false',
    style: 'position:absolute;width:0;height:0;overflow:hidden',
  });
  const defs = elem('defs', {});
  const OPRIRI = {
    aur: ['#fbefc4', '#e2c173', '#b88e3a', '#f0d890', '#a57a2c'],
    argint: ['#fafbfc', '#d3d6db', '#a1a6ae', '#e8eaed', '#8e939b'],
    roz: ['#fbe3d8', '#e3ae9a', '#bf806b', '#f1c6b4', '#a9695a'],
    negru: ['#4a4a4e', '#222225', '#0f0f11', '#34343a', '#141416'],
  };
  for (const [metal, culori] of Object.entries(OPRIRI)) {
    const grad = elem('linearGradient', { id: `mlx-metal-${metal}`, x1: '0', y1: '0', x2: '1', y2: '1' });
    culori.forEach((culoare, i) => {
      grad.append(elem('stop', { offset: `${Math.round((i / (culori.length - 1)) * 100)}%`, 'stop-color': culoare }));
    });
    defs.append(grad);
  }
  // Urma laserului: o muchie de lumina sub fiecare litera, ca la o gravura
  // adancita in metal. In milimetrii desenului (invelisul n-are transformare).
  const filtru = elem('filter', { id: 'mlx-gravat', x: '-5%', y: '-20%', width: '110%', height: '140%' });
  filtru.append(
    elem('feOffset', { in: 'SourceAlpha', dx: '0', dy: '0.09', result: 'jos' }),
    elem('feFlood', { 'flood-color': '#ffffff', 'flood-opacity': '0.38', result: 'lumina' }),
    elem('feComposite', { in: 'lumina', in2: 'jos', operator: 'in', result: 'muchie' }),
  );
  const unire = elem('feMerge', {});
  unire.append(elem('feMergeNode', { in: 'muchie' }), elem('feMergeNode', { in: 'SourceGraphic' }));
  filtru.append(unire);
  defs.append(filtru);

  // Metal PERIAT: fire fine de-a lungul piesei, ca pe otelul real (poza de
  // referinta a lui Vlad: cruce neagra periata, gravura alba). Zgomot intins
  // pe o axa, adus la gri si adunat peste culoare — ±9 % lumina, doar in
  // conturul piesei. In milimetri: ~2,4 fire pe milimetru.
  for (const [id, frecventa] of [['mlx-periat-v', '2.4 0.04'], ['mlx-periat-o', '0.04 2.4']]) {
    const periat = elem('filter', {
      id, x: '0', y: '0', width: '100%', height: '100%', 'color-interpolation-filters': 'sRGB',
    });
    periat.append(
      elem('feTurbulence', { type: 'fractalNoise', baseFrequency: frecventa, numOctaves: '2', seed: '7', result: 'zgomot' }),
      elem('feColorMatrix', {
        in: 'zgomot', type: 'matrix', result: 'gri',
        values: '0.33 0.33 0.33 0 0  0.33 0.33 0.33 0 0  0.33 0.33 0.33 0 0  0 0 0 0 1',
      }),
      elem('feComposite', {
        in: 'SourceGraphic', in2: 'gri', operator: 'arithmetic', k1: '0', k2: '1', k3: '0.2', k4: '-0.1', result: 'fibra',
      }),
      elem('feComposite', { in: 'fibra', in2: 'SourceAlpha', operator: 'in' }),
    );
    defs.append(periat);
  }
  svg.append(defs);
  document.body.append(svg);
}

/** Suprafata pe care incape scrisul. Aceleasi reguli ca `usableArea` din `src/mockup/shapes.js`. */
function zonaUtila(zona) {
  const marja = Number(zona.marjaMm ?? 1.2);
  const inaltime = Math.max(1, Number(zona.inaltimeMm) - marja * 2);
  if (zona.forma === 'cerc') {
    if (peCurbura(zona)) return arcUtila(zona, marja);
    const latura = Math.max(1, (Math.min(zona.latimeMm, zona.inaltimeMm) / 2 - marja) * Math.SQRT2);
    return { latimeMm: latura, inaltimeMm: latura };
  }
  // Rotit: „latimea" de scris e latura lunga, inaltimea randului e latura scurta.
  if (eVertical(zona)) {
    return {
      latimeMm: Math.max(1, Number(zona.inaltimeMm) - marja * 2),
      inaltimeMm: Math.max(1, Number(zona.latimeMm) - marja * 2),
    };
  }
  // La cruce, directia gravurii decide: vertical = textul rotit curge pe
  // bratul lung („latimea" de scris e inaltimea crucii); orizontal = text
  // drept pe toata latimea, la bratul transversal.
  if (eCruce(zona)) {
    const bratE = Math.min(Number(zona.bratMm ?? 9), Number(zona.latimeMm) || 9999);
    if (zona.mod !== 'vertical') {
      return {
        latimeMm: Math.max(1, Number(zona.latimeMm) - marja * 2),
        inaltimeMm: Math.max(1, bratE - marja * 2),
      };
    }
    return {
      latimeMm: Math.max(1, Number(zona.inaltimeMm) - marja * 2),
      inaltimeMm: Math.max(1, bratE - marja * 2),
    };
  }
  return { latimeMm: Math.max(1, Number(zona.latimeMm) - marja * 2), inaltimeMm: inaltime };
}

/** Cat aer se lasa in jurul zonei gravate, cel putin. */
const AER_MIN_MM = 1.5;

/**
 * Cat de mare poate fi piesa fata de zona ca sa o mai aratam intreaga.
 *
 * Masurat pe magazin, nu presupus: o bratara cu piesa de 210 x 9 mm si zona de 55 x 6 mm
 * dadea un viewBox de 23:1, adica un nume gravat inalt de 19 pixeli pe un ecran de 437.
 * Ilizibil. Pe un pandantiv de 20 x 35 mm conturul e insa informatie — se vede forma pe
 * care scrii — asa ca piesa se arata cand incape, si se decupeaza cand nu.
 */
const PIESA_MAXIM = 2.2;

/** Rama previzualizarii, in coordonatele piesei. */
function incadrare(g) {
  const aer = Math.max(AER_MIN_MM, Math.min(g.latimeZona, g.inaltimeZona) * 0.35);
  const peZona = {
    x: g.zonaX - aer,
    y: g.zonaY - aer,
    w: g.latimeZona + aer * 2,
    h: g.inaltimeZona + aer * 2,
  };

  /*
   * Pe MACHETA piesa se arata intreaga: acolo clientul se uita la bijuterie,
   * nu la cote. Decupata pe zona, picatura Umbrei iesea un arc taiat — cu
   * cadrele tehnice ascunse, semnul de rupere nu mai spunea nici macar ca e
   * decupata, deci arata a desen stricat.
   *
   * Exceptia ramane piesa foarte ALUNGITA: o bara de 210 × 9 mm, intreaga,
   * e o dunga in care gravura nu se mai citeste. Acolo decupajul pe zona e
   * singurul fel in care se vede ce scrie.
   */
  const raportPiesa = Math.max(g.latimePiesa / g.inaltimePiesa, g.inaltimePiesa / g.latimePiesa);
  const intreagaPeMacheta = g.macheta && raportPiesa <= 3.5;

  // Crucea se arata INTREAGA, oricat de mica ar iesi zona pe langa ea:
  // conturul crucii e chiar rostul desenului — decupata la bratul vertical,
  // clientul vedea o bara si nu intelegea ca alege o cruce.
  const piesaIncape = Number.isFinite(g.latimePiesa) && Number.isFinite(g.inaltimePiesa)
    && (g.eCruce || intreagaPeMacheta
      || (g.latimePiesa <= peZona.w * PIESA_MAXIM && g.inaltimePiesa <= peZona.h * PIESA_MAXIM));
  if (!piesaIncape) return peZona;

  const a = Math.max(AER_MIN_MM, Math.min(g.latimePiesa, g.inaltimePiesa) * 0.12);
  return { x: -a, y: -a, w: g.latimePiesa + a * 2, h: g.inaltimePiesa + a * 2 };
}

/**
 * Panza previzualizarii.
 *
 * Conturul piesei se deseneaza intotdeauna la marimea lui reala; cand rama e decupata pe zona,
 * el iese din cadru si se taie singur — asa se vad marginile de sus si de jos ale barei, si se
 * intelege ca piesa continua dincolo de ce se arata.
 */
/**
 * Ce forme poate purta conturul venit de la server, si ce atribute au voie.
 *
 * Serverul le-a verificat deja (`src/mockup/contur.js`), dar lista se scrie si
 * aici: regula acestui fisier e ca nimic venit de pe fir nu ajunge in pagina
 * fara sa treaca printr-o lista pe care o vedem cu ochii. Nu e markup, sunt
 * atribute — se pun cu `setAttribute`, nu cu `innerHTML`.
 */
const FORME_CONTUR = {
  path: ['d'],
  circle: ['cx', 'cy', 'r'],
  ellipse: ['cx', 'cy', 'rx', 'ry'],
  rect: ['x', 'y', 'width', 'height', 'rx', 'ry'],
  line: ['x1', 'y1', 'x2', 'y2'],
  polygon: ['points'],
  polyline: ['points'],
};

const D_CONTUR = /^[MmZzLlHhVvCcSsQqTtAa0-9eE.,\s+-]+$/;
const PUNCTE_CONTUR = /^[0-9eE.,\s+-]+$/;
const MATRICE_CONTUR = /^matrix\([-0-9.eE\s]+\)$/;

/**
 * Conturul REAL al bijuteriei, din desenul Illustrator.
 *
 * Inima din desen se vede ca o inima, nu ca un dreptunghi: exact forma pe care
 * o arata macheta trimisa in DM si fisierul care ajunge la laser. Vine ca lista
 * de forme in milimetri, cu originea in coltul piesei — acelasi sistem in care
 * sta zona, deci cele doua nu se pot desincroniza.
 */
function conturDinForme(forme) {
  const desenate = [];

  for (const forma of Array.isArray(forme) ? forme : []) {
    const permise = FORME_CONTUR[forma?.tip];
    if (!permise) continue;

    const atribute = { 'vector-effect': 'non-scaling-stroke' };
    let bune = true;

    for (const cheie of permise) {
      const valoare = forma.atribute?.[cheie];
      if (valoare === undefined || valoare === null) continue;

      if (cheie === 'd' || cheie === 'points') {
        const tipar = cheie === 'd' ? D_CONTUR : PUNCTE_CONTUR;
        if (typeof valoare !== 'string' || !tipar.test(valoare)) { bune = false; break; }
        atribute[cheie] = valoare;
      } else if (Number.isFinite(Number(valoare))) {
        atribute[cheie] = String(Number(valoare));
      } else {
        bune = false;
        break;
      }
    }

    if (!bune || Object.keys(atribute).length === 1) continue;
    if (typeof forma.transform === 'string' && MATRICE_CONTUR.test(forma.transform)) {
      atribute.transform = forma.transform;
    }
    desenate.push(elem(forma.tip, atribute));
  }

  if (desenate.length === 0) return null;
  const grup = elem('g', { class: 'gravura__piesa' });
  grup.append(...desenate);
  return grup;
}

/** Conturul piesei, dupa forma zonei. O bara se deseneaza cu capete rotunde. */
function conturPiesei(forma, latime, inaltime, raza, bratMm = null) {
  if (forma === 'cerc') {
    return elem('circle', {
      class: 'gravura__piesa', cx: latime / 2, cy: inaltime / 2,
      r: Math.min(latime, inaltime) / 2, 'vector-effect': 'non-scaling-stroke',
    });
  }
  if (forma === 'cruce') {
    return conturCrucii(0, 0, latime, inaltime, bratMm);
  }
  // Bara si tag: dreptunghi rotunjit. La o bara ingusta, raza e jumatate din
  // inaltime — adica exact capetele rotunde ale placutei reale.
  return elem('rect', {
    class: 'gravura__piesa', x: 0, y: 0, width: latime, height: inaltime,
    rx: Math.min(raza, inaltime / 2), 'vector-effect': 'non-scaling-stroke',
  });
}

/**
 * Conturul unei cruci, cu originea data. Bratul vertical plus cel orizontal,
 * la o treime de sus — aceeasi geometrie ca `zoneFrame` din motor.
 */
function conturCrucii(x, y, latime, inaltime, bratMm, clasa = 'gravura__piesa') {
  const brat = Number(bratMm) > 0
    ? Math.min(Number(bratMm), latime * 0.8)
    : Math.min(latime * 0.36, inaltime * 0.36);
  const stanga = x + (latime - brat) / 2;
  const sus = y + inaltime * 0.28;
  const d = [
    `M ${stanga} ${y}`, `H ${stanga + brat}`, `V ${sus}`, `H ${x + latime}`,
    `V ${sus + brat}`, `H ${stanga + brat}`, `V ${y + inaltime}`, `H ${stanga}`,
    `V ${sus + brat}`, `H ${x}`, `V ${sus}`, `H ${stanga}`, 'Z',
  ].join(' ');
  return elem('path', { class: clasa, d, 'vector-effect': 'non-scaling-stroke' });
}

/** Conturul zonei gravate, dupa forma. */
function conturZonei(forma, x, y, latime, inaltime, raza, bandaMm = null) {
  if (forma === 'cerc') {
    const r = Math.min(latime, inaltime) / 2;
    const cerc = elem('circle', {
      class: 'gravura__zona', cx: x + latime / 2, cy: y + inaltime / 2,
      r, 'vector-effect': 'non-scaling-stroke',
    });
    // Inelul isi arata gaura: fara ea, clientul ar vedea un disc plin.
    const banda = Number(bandaMm) > 0 && Number(bandaMm) < r ? Number(bandaMm) : null;
    if (!banda) return cerc;
    const grup = elem('g', {});
    grup.append(cerc, elem('circle', {
      class: 'gravura__zona', cx: x + latime / 2, cy: y + inaltime / 2,
      r: r - banda, 'vector-effect': 'non-scaling-stroke',
    }));
    return grup;
  }
  return elem('rect', {
    class: 'gravura__zona', x, y, width: latime, height: inaltime,
    rx: Math.min(raza, inaltime / 2), 'vector-effect': 'non-scaling-stroke',
  });
}

/**
 * Semnul de rupere: piesa continua dincolo de cadru.
 *
 * Fara el, o bara de 210 mm decupata la 55 lasa doua linii orizontale libere
 * care traversau desenul — clientul le citea ca pe niste chenare de camp, nu ca
 * pe marginile bratarii. Zig-zagul e conventia din desenul tehnic si se
 * intelege fara legenda.
 */
function semnDeRupere(x, sus, jos) {
  const pas = (jos - sus) / 4;
  const lat = pas * 0.55;
  const puncte = [];
  for (let i = 0; i <= 4; i += 1) {
    puncte.push(`${(x + (i % 2 === 0 ? -lat : lat)).toFixed(2)} ${(sus + pas * i).toFixed(2)}`);
  }
  return elem('path', {
    class: 'gravura__rupere',
    d: `M ${puncte.join(' L ')}`,
    'vector-effect': 'non-scaling-stroke',
  });
}

/**
 * Dreptunghiul in care chiar se scrie, in milimetrii piesei.
 *
 * Pe cruce urmeaza DIRECTIA gravurii (bratul lung sau transversalul); pe rest e
 * centrat in zona; pe curbura nu exista (textul urmeaza cercul) si iese zona
 * intreaga. Il folosesc si conturul punctat, si zoom-ul pe gravura.
 */
function dreptunghiulUtil(zona, dim) {
  const utila = zonaUtila({ ...zona, latimeMm: dim.latimeZona, inaltimeMm: dim.inaltimeZona });
  const marja = Number(zona.marjaMm ?? 1.2);
  if (eCruce(zona, dim.latimeZona, dim.inaltimeZona)) {
    const brat = Math.min(Number(zona.bratMm ?? 9), dim.latimeZona);
    return zona.mod !== 'vertical'
      ? {
        x: dim.zonaX + marja, y: dim.zonaY + dim.inaltimeZona * 0.28 + marja,
        w: Math.max(0, dim.latimeZona - marja * 2), h: Math.max(0, brat - marja * 2),
      }
      : {
        x: dim.zonaX + (dim.latimeZona - brat) / 2 + marja, y: dim.zonaY + marja,
        w: Math.max(0, brat - marja * 2), h: Math.max(0, dim.inaltimeZona - marja * 2),
      };
  }
  if (peCurbura(zona)) return { x: dim.zonaX, y: dim.zonaY, w: 0, h: 0, curbura: true };
  if (eVertical(zona)) {
    return {
      x: dim.zonaX + marja, y: dim.zonaY + marja,
      w: Math.max(0, dim.latimeZona - marja * 2), h: Math.max(0, dim.inaltimeZona - marja * 2),
    };
  }
  return {
    x: dim.zonaX + (dim.latimeZona - utila.latimeMm) / 2,
    y: dim.zonaY + (dim.inaltimeZona - utila.inaltimeMm) / 2,
    w: utila.latimeMm, h: utila.inaltimeMm,
  };
}

/**
 * Rama cu zoom pe gravura: suprafata pe care se scrie, cu putin metal in jur.
 *
 * Pe o cruce de 29 × 40 mm textul sta pe un brat de 6 mm; aratata intreaga,
 * crucea ocupa scena si gravura iese cat un rand de sub lupa. Zoom-ul arata
 * exact locul unde cade textul — piesa ramane in cadru, taiata de margini.
 */
function ramaCuZoom(zona, dim) {
  const util = dreptunghiulUtil(zona, dim);
  const tinta = util.curbura || util.w < 1 || util.h < 1
    ? { x: dim.zonaX, y: dim.zonaY, w: dim.latimeZona, h: dim.inaltimeZona }
    : util;
  const aer = Math.max(AER_MIN_MM, Math.min(tinta.w, tinta.h) * 0.45);
  return { x: tinta.x - aer, y: tinta.y - aer, w: tinta.w + aer * 2, h: tinta.h + aer * 2 };
}

function panza(rama, piesa, zona, dim) {
  const raza = Number(piesa.razaMm ?? zona.razaMm ?? CONTUR_RAZA_MM);
  const forma = zona.forma ?? 'bara';
  const cruce = eCruce(zona, dim.latimeZona, dim.inaltimeZona);
  const svg = elem('svg', {
    class: 'gravura__svg',
    viewBox: `${rama.x.toFixed(2)} ${rama.y.toFixed(2)} ${rama.w.toFixed(2)} ${rama.h.toFixed(2)}`,
    preserveAspectRatio: 'xMidYMid meet',
    'aria-hidden': 'true',
    focusable: 'false',
  });

  // Conturul din desenul Illustrator bate orice deducere: e forma reala a
  // bijuteriei. Se deseneaza doar ANCORAT (zona are offseturi din acelasi
  // desen) — aceeasi regula ca `pieceLayer` din motor, iar serverul trimite
  // conturul doar atunci.
  const formeContur = conturDinForme(piesa.contur);

  if (formeContur) {
    svg.append(formeContur);
    svg.append(cruce
      ? conturCrucii(dim.zonaX, dim.zonaY, dim.latimeZona, dim.inaltimeZona, zona.bratMm, 'gravura__zona')
      : conturZonei(forma === 'cruce' ? 'bara' : forma, dim.zonaX, dim.zonaY, dim.latimeZona, dim.inaltimeZona, zona.razaMm ?? raza, zona.bandaMm));
  } else if (cruce) {
    // Crucea desenata e ZONA masurata — aceeasi cruce (magenta) din fisierul
    // de productie — nu cutia piesei. Bara transversala sta la 28% din
    // inaltimea ZONEI, exact unde o pun si textul, si dreptunghiul punctat:
    // desenata din piesa (putin mai mare), bara iesea decalata cu o jumatate
    // de milimetru si dreptunghiul parea necentrat. Chenarul dreptunghiular
    // al zonei nu se deseneaza deloc: ar acoperi crucea fara sa spuna nimic.
    svg.append(conturCrucii(dim.zonaX, dim.zonaY, dim.latimeZona, dim.inaltimeZona, zona.bratMm));
  } else {
    // Formele care nu sunt cruce se deseneaza ca pana acum. Un sablon de cruce
    // cu bratul cat toata latimea intra tot aici — e un dreptunghi, si asa arata.
    const formaDesen = forma === 'cruce' ? 'bara' : forma;
    svg.append(conturPiesei(formaDesen, dim.latimePiesa, dim.inaltimePiesa, raza, zona.bratMm));
    svg.append(conturZonei(formaDesen, dim.zonaX, dim.zonaY, dim.latimeZona, dim.inaltimeZona, zona.razaMm ?? raza, zona.bandaMm));
  }

  // Suprafata utila, punctata: ce ramane dupa marja. Se deseneaza doar cand se vede.
  const utilaRect = dreptunghiulUtil(zona, dim);
  if (utilaRect.w > 1.5 && utilaRect.h > 1.5) {
    svg.append(elem('rect', {
      class: 'gravura__utila',
      x: utilaRect.x, y: utilaRect.y,
      width: utilaRect.w, height: utilaRect.h,
      'vector-effect': 'non-scaling-stroke',
    }));
  }

  // Capetele taiate de cadru primesc semnul de rupere.
  const sus = Math.max(rama.y, 0);
  const jos = Math.min(rama.y + rama.h, dim.inaltimePiesa);
  if (jos > sus) {
    if (rama.x > 0.5) svg.append(semnDeRupere(rama.x + rama.w * 0.035, sus, jos));
    if (rama.x + rama.w < dim.latimePiesa - 0.5) {
      svg.append(semnDeRupere(rama.x + rama.w * 0.965, sus, jos));
    }
  }

  return svg;
}

/**
 * Previzualizarea: piesa la scara ei, cu textul asezat in zona.
 *
 * Serverul face shaping-ul (ligaturile Great Vibes nu se pot reproduce in CSS) si trimite
 * contururile; asezarea o face browserul, la fiecare tasta. Fara impartirea asta, fiecare caracter
 * tastat de fiecare client ar insemna un fontkit si un `assertWellFormed` pe server.
 */
/**
 * @param {{macheta?: boolean}} [optiuni] `macheta` schimba doar INCADRAREA:
 *   piesa se arata intreaga, fiindca acolo se uita clientul la bijuterie.
 *   Culorile si cadrele ascunse sunt treaba lui `gravura.css`.
 */
function compune(raspuns, randuri = 1, optiuni = {}) {
  const piesa = raspuns.piesa ?? {};
  const zona = raspuns.zona ?? {};

  const latimePiesa = Number(piesa.latimeMm) || Number(zona.latimeMm) || 40;
  const inaltimePiesa = Number(piesa.inaltimeMm) || Number(zona.inaltimeMm) || 8;
  const latimeZona = Number(zona.latimeMm) || latimePiesa;
  const inaltimeZona = Number(zona.inaltimeMm) || inaltimePiesa;

  // Zona sta unde a pus-o desenul; fara desen, centrata pe piesa — aceeasi regula ca `plansa()`
  // din motor. Pe o cruce cu zona sus, un text centrat pe piesa ar cadea peste brate.
  const zonaX = Number.isFinite(zona.offsetXMm) ? zona.offsetXMm : (latimePiesa - latimeZona) / 2;
  const zonaY = Number.isFinite(zona.offsetYMm) ? zona.offsetYMm : (inaltimePiesa - inaltimeZona) / 2;

  const dim = { latimePiesa, inaltimePiesa, latimeZona, inaltimeZona, zonaX, zonaY };
  const ramaNormala = incadrare({
    latimePiesa, inaltimePiesa, latimeZona, inaltimeZona, zonaX, zonaY,
    eCruce: eCruce(zona, latimeZona, inaltimeZona),
    macheta: Boolean(optiuni.macheta),
  });
  // Zoom-ul are rost doar cand scena arata mai mult decat locul unde se scrie:
  // pe o bara deja decupata pe zona, ar fi acelasi desen.
  const ramaZoom = ramaCuZoom(zona, dim);
  const poateZoom = ramaNormala.w * ramaNormala.h > ramaZoom.w * ramaZoom.h * 1.6;
  const rama = optiuni.zoom && poateZoom ? ramaZoom : ramaNormala;
  const svg = panza(rama, piesa, zona, dim);
  // Raportul scenei, citit de CSS (`aspect-ratio`): scena ia forma piesei in loc
  // de o inaltime fixa. Nu depinde de text, deci nu tresare la tastare.
  svg.setAttribute('data-raport', (rama.w / rama.h).toFixed(4));
  if (poateZoom) svg.setAttribute('data-poate-zoom', '1');
  svg.setAttribute('data-fibra', fibraPiesei(latimePiesa, inaltimePiesa));
  // Metalul fetei, cand apelantul il stie (formularul, dupa varianta aleasa).
  if (optiuni.metal) svg.setAttribute('data-metal', optiuni.metal);

  // Culoarea gravurii de pe piesa reala: pe metal negru se graveaza alb, iar
  // desenul o arata la fel — piesa se innegreste si urma trece pe alb.
  if (zona.culoare === 'alb') svg.dataset.culoare = 'alb';

  // Simbolul din slot: serverul l-a scos din rand si l-a trimis separat, cu
  // milimetrii lui — se deseneaza exact unde l-a pus mesterul, pe piesa.
  const slot = raspuns.simbol_slot;
  if (slot && Array.isArray(slot.cai) && slot.cai.length > 0) {
    const scaraS = Number(slot.inaltimeMm) / (Number(slot.inaltimeUnitati) || 1);
    const xS = Number(slot.xMm) - ((Number(slot.latimeUnitati) || 0) * scaraS) / 2;
    const yS = Number(slot.yMm) - Number(slot.inaltimeMm) / 2 + (Number(slot.ascender) || 0) * scaraS;
    const grupS = elem('g', {
      class: 'gravura__urma',
      transform: `translate(${xS.toFixed(3)} ${yS.toFixed(3)}) scale(${scaraS.toFixed(6)} ${(-scaraS).toFixed(6)})`,
    });
    for (const cale of slot.cai) {
      const nod = caleaDin(cale);
      if (nod) grupS.append(nod);
    }
    // Invelisul fara transformare poarta filtrul de gravura: in milimetri, nu
    // in unitatile fontului — altfel muchia de lumina ar fi de un micron.
    const invelisS = elem('g', { class: 'gravura__gravat' });
    invelisS.append(grupS);
    svg.append(invelisS);
  }

  const cai = (raspuns.cai ?? []).map(caleaDin).filter(Boolean);
  if (cai.length === 0) return svg;

  // Randurile le spune serverul cand le-a rupt el; altfel, cate a scris omul.
  const randuriBloc = Number(raspuns.randuri) > 0 ? Number(raspuns.randuri) : randuri;
  const utila = zonaUtila({ ...zona, latimeMm: latimeZona, inaltimeMm: inaltimeZona });
  const latimeUnitati = Number(raspuns.latimeUnitati) || 1;
  const inaltimeUnitati = Number(raspuns.inaltimeUnitati) || 1;

  // Scara EXACTA a serverului (`scara`, mm pe unitate de font), pentru orice numar de randuri —
  // cu `fontScale` si cu a doua trecere de la cerc cu tot, pe care browserul nu le stie. Un server
  // mai vechi n-o trimite: atunci pe un rand se deduce din inaltimea literei, iar pe mai multe se
  // cade pe incadrarea de aici.
  const scaraServer = Number(raspuns.scara) > 0
    ? Number(raspuns.scara)
    : randuriBloc === 1 && Number.isFinite(raspuns.inaltime_litera_mm)
      ? raspuns.inaltime_litera_mm / inaltimeUnitati
      : null;
  const scara = scaraServer
    ?? Math.min(utila.latimeMm / latimeUnitati, utila.inaltimeMm / inaltimeUnitati);

  // Fonturile au y in sus, SVG-ul in jos: un singur grup rastoarna sistemul, exact ca in motor.
  const ascendent = Number(raspuns.ascendent ?? raspuns.ascender ?? inaltimeUnitati * 0.8);
  let transformare;
  const cruceReala = eCruce(zona, latimeZona, inaltimeZona);
  if (cruceReala && zona.mod === 'vertical') {
    // Pe bratul lung, textul se ROTESTE: translate + rotate(90) + scale(s,-s)
    // duce avansul literelor in jos si susul literelor spre dreapta — aceeasi
    // transformare ca in fisierul de productie.
    const gx = zonaX + latimeZona / 2 + (inaltimeUnitati * scara) / 2 - ascendent * scara;
    const gy = zonaY + (inaltimeZona - latimeUnitati * scara) / 2;
    transformare = `translate(${gx.toFixed(3)} ${gy.toFixed(3)}) rotate(90) scale(${scara.toFixed(6)} ${(-scara).toFixed(6)})`;
  } else if (cruceReala) {
    // Pe transversal: text drept, centrat pe latime, la randul barei.
    const bratT = Math.min(Number(zona.bratMm ?? 9), latimeZona);
    const barTop = zonaY + inaltimeZona * 0.28;
    const x = zonaX + (latimeZona - latimeUnitati * scara) / 2;
    const y = barTop + (bratT - inaltimeUnitati * scara) / 2 + ascendent * scara;
    transformare = `translate(${x.toFixed(3)} ${y.toFixed(3)}) scale(${scara.toFixed(6)} ${(-scara).toFixed(6)})`;
  } else if (eVertical(zona)) {
    // Bara/tag purtate pe verticala: aceeasi rotire ca pe bratul crucii, centrata
    // pe latimea zonei — textul curge de sus in jos, cum atarna piesa.
    const gx = zonaX + latimeZona / 2 + (inaltimeUnitati * scara) / 2 - ascendent * scara;
    const gy = zonaY + (inaltimeZona - latimeUnitati * scara) / 2;
    transformare = `translate(${gx.toFixed(3)} ${gy.toFixed(3)}) rotate(90) scale(${scara.toFixed(6)} ${(-scara).toFixed(6)})`;
  } else if (peCurbura(zona) && utila.arc) {
    // Pe curbura fiecare litera isi are unghiul ei, ca in `arcLines` din motor:
    // un bloc mutat intreg ar sta drept peste un cerc. Serverul trimite fiecare
    // glifa cu `translate(x y)` in unitati de font; de acolo se ia avansul.
    const jos = Boolean(utila.arc.jos);
    const cx = zonaX + latimeZona / 2;
    const cy = zonaY + inaltimeZona / 2;
    const pas = randuriBloc > 1 ? (inaltimeUnitati / (randuriBloc + 0.22 * (randuriBloc - 1))) * 1.22 * scara : 0;
    const razaBaza = jos ? utila.arc.razaMm - (randuriBloc - 1) * pas : utila.arc.razaMm;
    const desfasurare = (latimeUnitati * scara) / Math.max(0.5, razaBaza);
    const start = jos ? Math.PI / 2 + desfasurare / 2 : -Math.PI / 2 - desfasurare / 2;
    const grupArc = elem('g', { class: 'gravura__urma' });
    for (const cale of cai) {
      const t = cale.getAttribute('transform') || '';
      const mutare = t.match(/translate\(\s*(-?[\d.]+)[\s,]+(-?[\d.]+)\s*\)/);
      const gxU = mutare ? Number(mutare[1]) : 0;
      const gyU = mutare ? Number(mutare[2]) : 0;
      const restul = mutare ? t.slice(mutare.index + mutare[0].length).trim() : t;
      // Randul urmator (y negativ, in unitati de font) intra spre centru.
      const raza = Math.max(0.5, jos ? razaBaza - gyU * scara : razaBaza + gyU * scara);
      const unghi = jos ? start - (gxU * scara) / raza : start + (gxU * scara) / raza;
      const px = cx + raza * Math.cos(unghi);
      const py = cy + raza * Math.sin(unghi);
      const grade = (unghi * 180) / Math.PI + (jos ? -90 : 90);
      cale.setAttribute('transform',
        `translate(${px.toFixed(3)} ${py.toFixed(3)}) rotate(${grade.toFixed(3)}) `
        + `scale(${scara.toFixed(6)} ${(-scara).toFixed(6)})${restul ? ` ${restul}` : ''}`);
      grupArc.append(cale);
    }
    const invelisArc = elem('g', { class: 'gravura__gravat' });
    invelisArc.append(grupArc);
    svg.append(invelisArc);
    return svg;
  } else {
    const x = zonaX + (latimeZona - latimeUnitati * scara) / 2;
    const y = zonaY + (inaltimeZona - inaltimeUnitati * scara) / 2 + ascendent * scara;
    transformare = `translate(${x.toFixed(3)} ${y.toFixed(3)}) scale(${scara.toFixed(6)} ${(-scara).toFixed(6)})`;
  }
  const grup = elem('g', {
    class: 'gravura__urma',
    transform: transformare,
  });
  grup.append(...cai);
  const invelis = elem('g', { class: 'gravura__gravat' });
  invelis.append(grup);
  svg.append(invelis);
  return svg;
}

/**
 * Ce a scris clientul, pe handle de produs. Traieste peste re-randarile temei si peste plecarea
 * de pe produs — cateva zeci de octeti per produs vazut, in schimbul textului care nu se pierde.
 */
const STARE = new Map();

/** Cat traieste textul in browserul clientului. Dupa o saptamana e alt gand. */
const PASTRARE_MS = 7 * 24 * 60 * 60 * 1000;
const cheiaPastrarii = (handle) => `mlx_gravura_${handle}`;

/**
 * Starea de pe disc, daca exista si nu e veche.
 *
 * Reincarcarea paginii omora tot ce scrisese clientul — pe telefon se intampla
 * si fara voie (tab aruncat din memorie). Textul, fontul si cutia se pastreaza
 * in localStorage; verdictele si jetonul NU — ale sesiunii, se refac singure.
 */
const stareaDePeDisc = (handle) => {
  try {
    const bruta = JSON.parse(localStorage.getItem(cheiaPastrarii(handle)) || 'null');
    if (!bruta || bruta.v !== 1 || Date.now() - (bruta.cand || 0) > PASTRARE_MS) return null;
    return {
      texte: typeof bruta.texte === 'object' && bruta.texte ? bruta.texte : {},
      font: Number.isFinite(bruta.font) ? bruta.font : null,
      poza: typeof bruta.poza === 'string' ? bruta.poza : null,
      pozaCrop: bruta.pozaCrop && typeof bruta.pozaCrop === 'object' ? bruta.pozaCrop : null,
      impachetare: typeof bruta.impachetare === 'string' ? bruta.impachetare : '',
      impachetareDetaliu: typeof bruta.impachetareDetaliu === 'string' ? bruta.impachetareDetaliu : '',
      macheta: Boolean(bruta.macheta),
      maiTarziu: Boolean(bruta.maiTarziu),
    };
  } catch { return null; }
};

const stareaPentru = (handle) => {
  if (!STARE.has(handle)) {
    STARE.set(handle, {
      texte: {}, verdicte: {}, font: null, poza: null, pozaCrop: null, impachetare: '', impachetareDetaliu: '',
      macheta: false, maiTarziu: false, jeton: null, amprenta: null, nefunctional: false,
      ...(stareaDePeDisc(handle) ?? {}),
    });
  }
  return STARE.get(handle);
};

/**
 * Hexul, ca triplet „R G B".
 *
 * Tema isi scrie culorile asa (`--text-color: 28 28 28`) tocmai ca fiecare
 * regula sa poata compune cu transparenta: `rgb(var(--text-color) / 0.6)`.
 * Daca schimbam doar `color`-ul containerului, zecile de reguli care compun
 * asa raman pe culoarea VECHE — de aceea contorul, notele si cotele ieseau
 * negru-pe-negru pe un preset intunecat. Rescriind chiar tokenii, tot ce e
 * inauntru se recalculeaza singur, si al nostru, si al temei.
 */
const tripletRgb = (hex) => {
  const curat = String(hex ?? '').replace('#', '');
  if (!/^[0-9a-fA-F]{3}$|^[0-9a-fA-F]{6}$/.test(curat)) return null;
  const plin = curat.length === 3 ? curat.split('').map((c) => c + c).join('') : curat;
  const [r, g, b] = [0, 2, 4].map((i) => Number.parseInt(plin.slice(i, i + 2), 16));
  return `${r} ${g} ${b}`;
};

/**
 * Proba dintr-o adresa: `?aspect_flux=quiz`, `?aspect_mod=popup`.
 *
 * Fluxul e o setare a magazinului, deci ca sa vezi quiz-ul pe o tema de lucru
 * ar trebui sa-l pornesti pentru TOTI clientii. Parametrii astia il pornesc
 * doar in fila ta: nu se salveaza nicaieri, nu ating consola si nu schimba ce
 * vede altcineva. Se accepta doar valori din listele stiute.
 */
const suprascriereDinAdresa = (aspect) => {
  try {
    const parametri = new URLSearchParams(window.location.search);
    const flux = parametri.get('aspect_flux');
    const modCod = parametri.get('aspect_mod');
    const modGravura = parametri.get('aspect_mod_gravura');

    if (flux === 'quiz' || flux === 'separat') aspect.flux = flux;
    if (aspect.flux === 'quiz') { aspect.cod.mod = 'popup'; aspect.gravura.mod = 'popup'; }
    if (['panou', 'popup'].includes(modCod)) aspect.cod.mod = modCod;
    if (['desfasurat', 'pliat', 'popup'].includes(modGravura)) aspect.gravura.mod = modGravura;
    const prev = parametri.get('aspect_previzualizare');
    if (['plansa', 'buton', 'macheta', 'galerie'].includes(prev)) aspect.gravura.previzualizare = prev;
  } catch { /* fara parametri, ramane ce spune consola */ }
  return aspect;
};

/** Umbrele reglate din consola, in aceleasi trepte peste tot. */
const UMBRE_ASPECT = {
  fara: null,
  moale: '0 6px 20px rgba(0, 0, 0, 0.12)',
  tare: '0 14px 38px rgba(0, 0, 0, 0.28)',
};

class FormularGravura extends HTMLElement {
  connectedCallback() {
    this.opritor = new AbortController();
    this.semnal = this.opritor.signal;
    this.formular = this.closest('form[action*="/cart/add"]');
    this.stare = stareaPentru(this.dataset.handle);
    this.vorbe = JSON.parse(this.querySelector('[data-rol="vorbe"]').textContent);
    // Magazinul randeaza doar RO/EN, dar jumatate din clientii din Moldova au
    // browserul pe rusa. Etichetele RU exista deja (metaobiecte + vorbe-ru) —
    // se folosesc cand pagina e RO si browserul spune ru.
    const vorbeRu = this.querySelector('[data-rol="vorbe-ru"]');
    if (vorbeRu && this.dataset.limba === 'ro' && /^ru/i.test(navigator.language || '')) {
      try {
        this.vorbe = { ...this.vorbe, ...JSON.parse(vorbeRu.textContent) };
        this.dataset.limba = 'ru';
      } catch { /* vorbe-ru stricat: ramane romana */ }
    }
    this.corp = this.querySelector('[data-rol="corp"]');
    this.ceasuri = new Map();
    this.campuri = new Map();
    this.ascunse = new Map();

    // Elementul e proaspat, deci inputurile ascunse ale jetonului de dinainte nu mai sunt in
    // pagina — dar `STARE` tine minte ca s-a validat. Fara randurile astea, o schimbare de produs
    // urmata de un click pe „adauga" ar trimite in cos bijuteria FARA nicio proprietate de
    // gravura, si nimeni n-ar avea de unde sti.
    this.stare.jeton = null;
    this.stare.amprenta = null;
    for (const ramas of this.querySelectorAll('input[type="hidden"]')) ramas.remove();

    this.closest('product-form')?.addEventListener(
      'submit', (ev) => this.laTrimitere(ev), { capture: true, signal: this.semnal },
    );
    // Practic fiecare piesa poarta UN simbol. Apasarea lui in desen il schimba
    // cu urmatorul din lista — fara sa cauti butonul, fara sa stergi din text.
    this.addEventListener('click', (ev) => {
      const desen = ev.target.closest?.('[data-simbol]');
      if (desen) this.schimbaSimbolul(desen);
    }, { signal: this.semnal });
    // Dupa o schimbare de varianta, <buy-buttons> e inlocuit cu unul proaspat, care nu stie ca
    // noi blocam adaugarea. Semnul se pune la loc.
    this.formular?.addEventListener('product:rerender', () => {
      setTimeout(() => { this.aplicaBlocajul(); this.actualizeazaMetalul(); }, 0);
    }, { signal: this.semnal });
    // Alta culoare aleasa = alt metal in previzualizare. Selectorul de variante
    // al temei poate sta si in afara formularului (legat prin `form=`), deci se
    // asculta pe document; recitirea e ieftina si nu atinge reteaua.
    const recitesteMetalul = () => setTimeout(() => this.actualizeazaMetalul(), 30);
    document.addEventListener('change', recitesteMetalul, { signal: this.semnal });
    document.addEventListener('variant:change', recitesteMetalul, { signal: this.semnal });

    this.incarcaConfig();
    this.aplicaAspect();
  }

  /**
   * Stilistica reglata din consola (/dashboard/aspect). Ruleaza in paralel cu
   * incarcarea configuratiei — atinge alt teritoriu (clasele si variabilele
   * gazdei, plus eventuala strangere in buton), nu campurile.
   */
  async aplicaAspect() {
    try {
      // Din radacina pietei, ca toate celelalte cereri (/ro-md/apps/gravura/…).
      const raspuns = await fetch(this.adresa('aspect'));
      if (!raspuns.ok) return;
      const intreg = suprascriereDinAdresa(await raspuns.json());
      const aspect = intreg?.gravura;
      if (!aspect) return;

      const pune = (nume, valoare, clasa) => {
        if (!valoare) return;
        this.style.setProperty(nume, valoare);
        if (clasa) this.classList.add(clasa);
      };

      // Gradientul bate culoarea simpla: e alegerea mai specifica a omului.
      const accent = aspect.gradientDeLa && aspect.gradientLa
        ? `linear-gradient(${aspect.gradientUnghi ?? 135}deg, ${aspect.gradientDeLa}, ${aspect.gradientLa})`
        : aspect.accent;

      // Tokenii temei, rescrisi INAUNTRUL formularului: asa se recoloreaza si
      // ce nu stim ca exista — contorul, notele, cotele planșei, conturul
      // campurilor. Fara asta, un preset intunecat lasa jumatate din text
      // negru pe negru.
      const textTriplet = tripletRgb(aspect.text);
      const fundalTriplet = tripletRgb(aspect.fundal);
      if (textTriplet) this.style.setProperty('--text-color', textTriplet);
      if (fundalTriplet) this.style.setProperty('--background', fundalTriplet);

      pune('--gravura-fundal', aspect.fundal, 'gravura--fundal');
      pune('--gravura-text', aspect.text, 'gravura--text');
      pune('--gravura-accent', accent, 'gravura--accent');
      pune('--gravura-accent-text', aspect.accentText, null);
      pune('--gravura-umbra', UMBRE_ASPECT[aspect.umbra], 'gravura--umbra');
      if (aspect.radius !== null && aspect.radius !== undefined) {
        this.style.setProperty('--gravura-radius', `${aspect.radius}px`);
        this.classList.add('gravura--radius');
      }

      // Cele patru infatisari ale previzualizarii. Acelasi desen viu in toate
      // — se schimba doar unde sta si cat de tehnic arata.
      //
      // Doua dintre ele muta PLANSA, iar plansa se naste in `construieste`, pe
      // celalalt fir. `/aspect` e un JSON mic si castiga aproape mereu cursa cu
      // `/config`, deci facute aici s-ar fi facut peste un formular gol — si
      // n-ar fi facut nimic, tacut. De aceea asteapta „gata".
      this.previzualizare = aspect.previzualizare;
      if (aspect.previzualizare === 'buton') this.candEGata(() => this.plansaCaButon());
      if (aspect.previzualizare === 'galerie') this.candEGata(() => this.plansaInGalerie());
      // Macheta n-are ce astepta: e o clasa pe gazda, iar culorile sunt in
      // `gravura.css` — se aplica si desenului care apare dupa.
      if (aspect.previzualizare === 'macheta') this.classList.add('gravura--macheta');

      // In fluxul quiz corpul asta pleaca in popup-ul de comanda, la pasul 1 —
      // deci NU si-l mai strange singur. Doua feluri de a-l ascunde ar insemna
      // un formular care se deschide dintr-un buton si nu e nicaieri.
      if (intreg.flux === 'quiz') return;
      if (aspect.mod === 'pliat' || aspect.mod === 'popup') this.strangeFormularul(aspect);
    } catch { /* stilul temei ramane */ }
  }

  /**
   * Modurile „pliat" si „popup": corpul formularului sta dupa un buton.
   * Antetul ramane in pagina — spune CA piesa se personalizeaza; corpul vine
   * la apasare. Verificarile, jetonul si blocajul de adaugare raman neatinse:
   * campurile exista in DOM in ambele moduri.
   */
  strangeFormularul(aspect) {
    if (this.comutaNod || !this.corp) return;

    const limba = this.dataset.limba === 'en' ? 'en' : 'ro';
    const eticheta = aspect.eticheta?.[limba]
      || (limba === 'en' ? 'Personalise the engraving' : 'Personalizează gravura');

    const buton = document.createElement('button');
    buton.type = 'button';
    buton.className = 'button button--outline gravura__comuta';
    buton.textContent = eticheta;
    this.comutaNod = buton;

    if (aspect.mod === 'popup') {
      const dialog = document.createElement('dialog');
      dialog.className = 'gravura__dialog';

      const inchide = document.createElement('button');
      inchide.type = 'button';
      inchide.className = 'gravura__dialog-inchide';
      inchide.setAttribute('aria-label', limba === 'en' ? 'Close' : 'Închide');
      inchide.textContent = '×';
      inchide.addEventListener('click', () => dialog.close(), { signal: this.semnal });

      this.corp.insertAdjacentElement('beforebegin', buton);
      dialog.append(inchide, this.corp);
      this.append(dialog);
      buton.addEventListener('click', () => dialog.showModal(), { signal: this.semnal });
      return;
    }

    this.corp.hidden = true;
    buton.setAttribute('aria-expanded', 'false');
    this.corp.insertAdjacentElement('beforebegin', buton);
    buton.addEventListener('click', () => {
      const inchis = this.corp.hidden;
      this.corp.hidden = !inchis;
      buton.setAttribute('aria-expanded', String(inchis));
    }, { signal: this.semnal });
  }

  disconnectedCallback() {
    for (const ceas of this.ceasuri.values()) clearTimeout(ceas);
    clearTimeout(this.ceasValidare);
    clearTimeout(this.ceasMostre);
    this.opresteGhidul();
    this.opritor.abort();
    // Stratul din vitrina traieste in AFARA elementului, deci nu pleaca odata
    // cu el: la re-randarea produsului ar ramane o machetă orfana peste poza.
    this.oglinda?.remove();
    this.oglinda = null;
  }

  vorba(cheie, inlocuiri = {}) {
    let text = this.vorbe[cheie] ?? '';
    for (const [nume, val] of Object.entries(inlocuiri)) text = text.replaceAll(`{${nume}}`, val);
    return text;
  }

  // Adresele se compun mereu din `Shopify.routes.root`: magazinul randeaza /ro-md/…, iar un
  // /apps/gravura scris fix pierde contextul de piata.
  adresa(cale) { return `${radacina()}apps/gravura/${cale}`; }

  async cere(cale, optiuni) {
    const raspuns = await fetch(this.adresa(cale), { ...optiuni, signal: this.semnal });
    // 422 e un raspuns, nu o defectiune: asa intoarce `/gravura/valideaza` erorile pe campuri.
    // Tratat ca esec de retea, motivul „nu incape" ar deveni „mai incearca o data".
    if (!raspuns.ok && raspuns.status !== 422) throw new Error(`${cale}: ${raspuns.status}`);
    return raspuns.json();
  }

  cloneaza(nume) {
    return this.querySelector(`[data-sablon="${nume}"]`).content.firstElementChild.cloneNode(true);
  }

  // Pagina are patru formulare catre /cart/add; fara prefix, id-urile s-ar calca reciproc si
  // eticheta ar trimite la campul altui formular.
  identificator(sufix) { return `${this.dataset.formular || 'gravura'}-gravura-${sufix}`; }

  async incarcaConfig() {
    try {
      // Tara hotaraste moneda si pretul cutiilor: magazinul tine preturile in MDL, dar are si
      // piata RO, unde aceeasi cutie costa 67, nu 250. Serverul cere pretul contextual.
      // Tara PIETEI, scrisa de Liquid; `window.Shopify.country` ramane doar
      // rezerva, pentru cazul in care atributul lipseste.
      const tara = this.dataset.tara || window.Shopify?.country || '';
      this.config = await this.cere(
        `config?handle=${encodeURIComponent(this.dataset.handle)}`
        + (tara ? `&tara=${encodeURIComponent(tara)}` : '')
        // Ideile de gravura sunt text citit de om: serverul trebuie sa stie
        // in ce limba e pagina, altfel un client englez primeste sugestii
        // romanesti.
        + `&limba=${encodeURIComponent(this.dataset.limba === 'en' ? 'en' : 'ro')}`,
      );
      // Un link partajat aduce textele si fontul INAINTE de construire, ca sa
      // apara in campuri de la prima randare.
      this.citesteLinkul();
      this.construieste();
    } catch {
      if (this.semnal.aborted) return;
      // Un API cazut nu opreste vanzarea: comanda intra fara text si ajunge in coada
      // `lipsa_date` din consola, unde o vede un om. Mai bine decat un buton mort.
      this.stare.nefunctional = true;
      this.spuneStarea(this.vorba('eroareConfig'));
    }
  }

  /**
   * Pretul unei cutii, in moneda pietei.
   *
   * Cifra vine gata convertita de Shopify (`contextualPricing`), nu inmultita de noi cu cursul:
   * Shopify rotunjeste IN SUS, deci 300 MDL fac 81 RON, nu 80. Masurat pe cos, pe toate patru.
   */
  pretulCutiei(cutie) {
    if (!Number.isFinite(cutie.pret) || !cutie.moneda) return '';
    // „0 MDL" arata a pret negociat prost. Ce e inclus se scrie ca atare.
    if (cutie.pret === 0) return this.vorba('gratis');

    // Doua monede pe acelasi ecran nu se explica nimanui. Daca pretul cutiei
    // nu vine in moneda in care e scris produsul, cutia ramane FARA pret —
    // se alege dupa nume, iar cifra o vede la coș. O lipsa se intreaba; un
    // „67 RON" sub un produs in MDL se calculeaza gresit in cap.
    const monedaPaginii = this.dataset.moneda || window.Shopify?.currency?.active || '';
    if (monedaPaginii && cutie.moneda !== monedaPaginii) return '';

    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'ro', {
        style: 'currency', currency: cutie.moneda, maximumFractionDigits: 0,
      }).format(cutie.pret);
    } catch {
      return `${Math.round(cutie.pret)} ${cutie.moneda}`;
    }
  }

  construieste() {
    this.stare.nefunctional = false;
    this.stare.font ??= this.config.fonturi?.[0]?.id ?? null;
    this.corp.replaceChildren();
    this.scene = new Map();
    this.ultimeleAsezari = new Map();
    this.nasturZoom = null;

    this.umpleri = new Map();
    this.sugestiiPuse = false;

    // Metalul piesei (gradientii traiesc o data pe pagina) si varianta aleasa.
    asiguraDefinitiile();
    this.actualizeazaMetalul();

    // Produs marcat „fara gravura" in consola: nu se graveaza, deci fara planșă,
    // fara texte, fara fonturi. Ramane DOAR alegerea cutiei — de aceea exista
    // formularul pe pagina asta. Antetul spune „Impachetare", nu „Gravura".
    if (this.config.fara_gravura) {
      const antet = this.querySelector('.gravura__antet .smallcaps');
      if (antet) antet.textContent = this.vorba('impachetare');
      this.querySelector('.gravura__reteta')?.remove();
      this.querySelector('[data-rol="ghid"]')?.remove();
      if ((this.config.impachetari ?? []).length > 0) {
        const pasCutie = this.pas(1, 'pasCutie', this.vorba('optional'));
        pasCutie.corp.append(this.alegereaImpachetarii());
        this.totalNod = document.createElement('p');
        this.totalNod.className = 'gravura__total';
        this.totalNod.setAttribute('aria-live', 'polite');
        this.totalNod.hidden = true;
        pasCutie.corp.append(this.totalNod);
        this.corp.append(pasCutie.nod);
      } else {
        // Nici cutii: nu e nimic de aratat, elementul se strange.
        this.hidden = true;
      }
      this.stareaGenerala = document.createElement('p');
      this.stareaGenerala.className = 'gravura__stare text-sm';
      this.stareaGenerala.setAttribute('role', 'status');
      this.stareaGenerala.setAttribute('aria-live', 'polite');
      this.stareaGenerala.hidden = true;
      this.corp.append(this.stareaGenerala);
      this.aplicaBlocajul();
      this.actualizeazaTotalul();
      this.dataset.gata = '1';
      this.dispatchEvent(new CustomEvent('gravura:gata'));
      return;
    }

    // Previzualizarea sta LIPITA sus: cat derulezi prin optiuni, vezi ce faci.
    // Inainte iesea din ecran la primul camp, si alegeai fontul pe nevazute.
    this.corp.append(this.plansa());

    // Pasul 1 — ce se graveaza. Toate campurile retetei, intr-un singur grup:
    // fata si spatele sunt aceeasi decizie, nu doua.
    const pasText = this.pas(1, 'pasText');
    for (const camp of this.config.campuri ?? []) {
      pasText.corp.append(this.estePoza(camp) ? this.campPoza(camp) : this.campText(camp));
    }
    pasText.nod.dataset.ghid = 'text';
    this.corp.append(pasText.nod);
    this.pasi = { text: pasText.nod };

    let numar = 1;
    if ((this.config.fonturi ?? []).length > 0) {
      numar += 1;
      const pasFont = this.pas(numar, 'pasFont');
      pasFont.corp.append(this.alegereaFontului());
      pasFont.nod.dataset.ghid = 'font';
      this.corp.append(pasFont.nod);
      this.pasi.font = pasFont.nod;
    }
    if ((this.config.impachetari ?? []).length > 0) {
      numar += 1;
      const pasCutie = this.pas(numar, 'pasCutie', this.vorba('optional'));
      this.pasi.cutie = pasCutie.nod;
      pasCutie.corp.append(this.alegereaImpachetarii());

      // Cat plateste, sub cutii. Pretul de sus ramane al bijuteriei — asa il
      // stie tema si asa il stie Shopify — dar cine adauga o cutie de 300 vedea
      // pana acum tot 800 pe ecran si afla abia la coș.
      this.totalNod = document.createElement('p');
      this.totalNod.className = 'gravura__total';
      this.totalNod.setAttribute('aria-live', 'polite');
      this.totalNod.hidden = true;
      pasCutie.corp.append(this.totalNod);

      pasCutie.nod.dataset.ghid = 'impachetare';
      this.corp.append(pasCutie.nod);
    }

    // Rezumatul: ce pleaca in cos, intr-un singur loc, chiar deasupra butonului.
    this.corp.append(this.rezumat());

    // „Alte optiuni" (macheta inainte de gravare, textul trimis mai tarziu) au
    // fost scoase pe 26 sep 2026, cerut de Vlad: doua bife pe care nu le atingea
    // aproape nimeni, sub un titlu care nu spunea ce ascunde. Starea lor ramane
    // stinsa — si cea salvata pe disc de o vizita mai veche.
    this.stare.macheta = false;
    this.stare.maiTarziu = false;

    // „Gravat si expediat in 1-2 zile" — raspunsul la intrebarea pe care
    // clientii o pun oricum in DM. Vine din setarile serverului; fara setare,
    // nu apare nimic.
    if (this.config.livrare) {
      const linie = document.createElement('p');
      linie.className = 'gravura__livrare text-xs';
      linie.textContent = this.config.livrare;
      this.corp.append(linie);
    }
    this.pornesteGhidul();
    // Cu un text deja scris (refresh, link partajat), fonturile il scriu din prima.
    this.cereMostrele(this.textulMostrelor());

    this.stareaGenerala = document.createElement('p');
    this.stareaGenerala.className = 'gravura__stare text-sm';
    this.stareaGenerala.setAttribute('role', 'status');
    this.stareaGenerala.setAttribute('aria-live', 'polite');
    this.stareaGenerala.hidden = true;
    this.corp.append(this.stareaGenerala);

    this.actualizeazaFonturi();
    this.aplicaBlocajul();
    this.actualizeazaRezumatul();
    // Fata cu poza, daca exista una aleasa deja in sesiunea asta (schimbare de
    // varianta: formularul se reface, starea ramane).
    this.deseneazaFataCuPoza();

    // Campurile exista de-acum. Fluxul quiz muta corpul asta in popup-ul de
    // comanda, si trebuie sa stie CAND are ce muta. `data-gata` acopera si
    // cazul in care ascultatorul se pune dupa ce randarea s-a terminat.
    this.dataset.gata = '1';
    this.dispatchEvent(new CustomEvent('gravura:gata'));
  }

  /**
   * Previzualizarea, una singura, cu cate o fasie per fata gravata.
   *
   * Inainte fiecare camp avea planșa lui. Pe o piesa gravata fata-spate ieseau doua desene
   * identice ca chenar, la 121 px fiecare, si formularul ajungea la 948 px inaltime. Mai rau
   * decat spatiul: doua planse spun ca sunt doua obiecte, cand piesa e una cu doua fete.
   */
  /**
   * Previzualizarea „la cerere": plansa vie se ascunde, iar in locul ei sta
   * „Vezi macheta". Desenul continua sa se refaca la fiecare tasta — doar ca
   * nevazut — asa ca popupul arata intotdeauna versiunea curenta.
   */
  plansaCaButon() {
    const fete = this.querySelector('[data-rol="fete"]');
    const plansa = fete?.closest('.gravura__plansa');
    if (!plansa || this.nasturuleMacheta) return;

    plansa.classList.add('gravura__plansa--nevazuta');
    const buton = document.createElement('button');
    buton.type = 'button';
    buton.className = 'button button--outline gravura__vezi';
    buton.textContent = this.vorba('veziMacheta');
    buton.addEventListener('click', () => this.arataMacheta(), { signal: this.semnal });
    plansa.insertAdjacentElement('beforebegin', buton);
    this.nasturuleMacheta = buton;
  }

  /**
   * Ruleaza cand corpul formularului e construit — acum, daca e deja.
   *
   * `construieste` anunta cu `gravura:gata`; pana atunci nu exista nici plansa,
   * nici campurile. Orice atinge corpul din `aplicaAspect` trece pe aici.
   */
  candEGata(fapta) {
    if (this.dataset.gata === '1') { fapta(); return; }
    this.addEventListener('gravura:gata', fapta, { once: true, signal: this.semnal });
  }

  /**
   * Desenam macheta (fond intunecat, piesa intreaga) sau desenul tehnic?
   *
   * Si `galerie` e machetă: stratul din vitrina poarta aceleasi culori, iar
   * desenul din el e clonat de aici — daca incadrarea ar ramane cea tehnica,
   * peste poza produsului ar ajunge o piesa decupata.
   */
  aratamMacheta() {
    return this.previzualizare === 'macheta' || this.previzualizare === 'galerie';
  }

  /**
   * Previzualizarea in VITRINA: formularul ramane fara desen, iar macheta se
   * asaza peste PRIMA poza a produsului, din clipa in care clientul scrie.
   *
   * Nu se atinge galeria temei — nici sursa pozei, nici lista de medii, nici
   * caruselul. Se pune un strat deasupra primului cadru; cand omul sterge tot,
   * stratul dispare si poza ramane cum era. O galerie rescrisa de tema (la
   * schimbarea de varianta) nu lasa doua straturi: cele vechi se sterg intai,
   * iar `actualizeazaOglinda` isi reface stratul daca a fost aruncat.
   */
  plansaInGalerie() {
    const fete = this.querySelector('[data-rol="fete"]');
    const plansa = fete?.closest('.gravura__plansa');
    const gazda = document.querySelector('.product-gallery__media, [data-media-id]');
    if (!plansa || !gazda) return;

    plansa.classList.add('gravura__plansa--nevazuta');
    for (const veche of gazda.querySelectorAll('.gravura__oglinda')) veche.remove();

    // Stratul se aseaza absolut; daca gazda nu e pozitionata, s-ar duce dupa
    // primul stramos pozitionat — adica peste tot caruselul.
    if (getComputedStyle(gazda).position === 'static') gazda.style.position = 'relative';

    const oglinda = document.createElement('div');
    oglinda.className = 'gravura__oglinda gravura--macheta';
    oglinda.hidden = true;
    gazda.append(oglinda);
    this.oglinda = oglinda;
    this.actualizeazaOglinda();
  }

  /**
   * Ce se vede in vitrina: desenele fetelor care au text, clonate. Clonele, nu
   * nodurile: desenul din formular ramane sursa unica, iar daca modul se
   * schimba nu ramane nimic mutat aiurea.
   */
  actualizeazaOglinda() {
    if (!this.oglinda) return;
    // Tema rescrie galeria la schimbarea de varianta si arunca stratul cu ea.
    if (!this.oglinda.isConnected) {
      this.oglinda = null;
      this.plansaInGalerie();
      return;
    }

    // Doar desenele cu textul clientului: piesa goala si exemplul estompat nu
    // au ce cauta peste poza produsului.
    const desene = [...(this.scene?.values() ?? [])]
      .map((scena) => scena.querySelector('svg.gravura__svg:not(.gravura__svg--gol)'))
      .filter(Boolean)
      .map((svg) => svg.cloneNode(true));

    this.oglinda.dataset.metal = this.dataset.metal ?? 'aur';
    this.oglinda.replaceChildren(...desene);
    this.oglinda.hidden = desene.length === 0;
  }

  /** Popupul cu macheta: clonele scenelor, marite, cu textul de-acum. */
  arataMacheta() {
    this.dialogMacheta?.remove();
    const dialog = document.createElement('dialog');
    dialog.className = 'gravura__dialog-macheta';

    const inchide = document.createElement('button');
    inchide.type = 'button';
    inchide.className = 'gravura__dialog-inchide';
    inchide.setAttribute('aria-label', this.vorba('inchide') || '\u00d7');
    inchide.textContent = '\u00d7';
    inchide.addEventListener('click', () => dialog.close());
    dialog.append(inchide);

    let desenate = 0;
    const multe = (this.scene?.size ?? 0) > 1;
    for (const [cod, scena] of this.scene ?? []) {
      // Exemplul estompat nu intra: fereastra arata gravura CLIENTULUI.
      const svg = scena.querySelector('.gravura__svg:not(.gravura__svg--gol)');
      if (!svg) continue;
      const clona = svg.cloneNode(true);
      clona.classList.add('gravura__svg--mare');
      clona.classList.remove('gravura__svg--nou');
      clona.removeAttribute('style');
      const figura = document.createElement('figure');
      figura.className = 'gravura__dialog-fata';
      figura.append(clona);
      // Pe o piesa cu doua fete, fiecare desen isi spune numele.
      const camp = (this.config?.campuri ?? []).find((c) => c.cod === cod);
      if (multe && camp) {
        const nume = document.createElement('figcaption');
        nume.className = 'smallcaps';
        nume.textContent = this.vorba(`parte${this.mareLaInceput(camp.parte)}`) || camp.parte;
        figura.append(nume);
      }
      dialog.append(figura);
      desenate += 1;
    }
    if (desenate === 0) {
      const gol = document.createElement('p');
      gol.className = 'gravura__dialog-gol';
      gol.textContent = this.vorba('machetaGoala');
      dialog.append(gol);
    }

    dialog.addEventListener('click', (ev) => { if (ev.target === dialog) dialog.close(); });
    dialog.addEventListener('close', () => dialog.remove());
    this.append(dialog);
    this.dialogMacheta = dialog;
    dialog.showModal();
  }

  plansa() {
    const nod = this.cloneaza('plansa');
    const gazda = nod.querySelector('[data-rol="fete"]');
    // Uneltele intai: fasiile de mai jos se deseneaza pe loc si isi cer zoom-ul.
    this.uneltelePlansei(nod.querySelector('[data-rol="scena-unelte"]'));
    const texte = (this.config.campuri ?? []).filter((camp) => !this.estePoza(camp));
    /*
     * Fata cu POZA intra si ea in plansa.
     *
     * Pana pe 9 sep 2026 previzualizarea arata doar fetele cu text: pe o piesa
     * „poza pe fata, text pe spate", clientul incarca ochii si vedea in desen
     * doar textul de pe spate. Macheta din rezumatul comenzii ii arata insa
     * ambele fete — doua raspunsuri diferite la aceeasi intrebare, in aceeasi
     * pagina. Acum plansa e ca macheta: fiecare suprafata gravata, in ordinea ei.
     */
    const cuPoza = (this.config.campuri ?? []).filter((camp) => this.estePoza(camp) && camp.zona);
    const fete = [...(this.config.campuri ?? [])].filter((camp) => texte.includes(camp) || cuPoza.includes(camp));

    for (const camp of fete) {
      const fasie = this.cloneaza('fata');
      const nume = fasie.querySelector('[data-rol="nume"]');
      // Numele fetei se scrie doar cand sunt mai multe: pe o piesa cu o singura fata,
      // eticheta „FAȚĂ" nu adauga nimic si mai ia un rand.
      if (fete.length > 1) nume.textContent = this.vorba(`parte${this.mareLaInceput(camp.parte)}`);
      else nume.remove();
      gazda.append(fasie);
      fasie.dataset.fasie = camp.cod;
      const scena = fasie.querySelector('[data-rol="scena"]');
      scena.dataset.cod = camp.cod;
      this.scene.set(camp.cod, scena);
      this.umpleri.set(camp.cod, fasie.querySelector('[data-rol="umplere"]'));
      // Umplerea e o masura a TEXTULUI; pe fata cu poza n-are ce arata.
      if (this.estePoza(camp)) fasie.querySelector('[data-rol="umplere"]')?.remove();
      this.golesteScena(camp.cod);
    }
    // Numele fetei se scrie mereu cand sunt mai multe, ca fasia sa poata fi
    // legata de campul ei — pe o singura fata ramane fara, cum era.
    this.fasii = gazda;

    // Partajarea sta langa cote, in subsolul planșei: e despre CE s-a compus.
    const unelte = nod.querySelector('[data-rol="unelte"]');
    if (unelte) unelte.append(this.nasturePartajare());

    this.cotaPlansa = nod.querySelector('[data-rol="cota"]');
    const spus = [];
    const zona = texte[0];
    if (zona?.estimat) spus.push(this.vorba('estimat'));
    this.cotaPlansa.textContent = spus.join(' · ');
    if (texte.length === 0) nod.hidden = true;
    return nod;
  }

  /**
   * Un pas al configuratorului.
   *
   * `<fieldset>` cu `<legend>`, nu un `<div>` cu text ingrosat: cititorul de
   * ecran anunta grupul inainte de primul camp, iar cine vede ecranul citeste
   * un titlu, nu inca o eticheta.
   */
  pas(numar, cheie, nota = null) {
    const nod = this.cloneaza('pas');
    nod.querySelector('[data-rol="numar"]').textContent = String(numar);
    nod.querySelector('[data-rol="titlu"]').textContent = this.vorba(cheie);
    const notaNod = nod.querySelector('[data-rol="pas-nota"]');
    if (nota) notaNod.textContent = nota; else notaNod.remove();
    return { nod, corp: nod.querySelector('[data-rol="pas-corp"]') };
  }

  /**
   * „Gravura ta" — rezumatul, chiar deasupra butonului de adaugare.
   *
   * Dupa trei pasi, omul nu mai tine minte ce a ales la primul. Un rezumat
   * scurt, cu textul EXACT asa cum se graveaza (cu ruperea pe randuri), fontul,
   * cutia si totalul, raspunde la „am pus tot?" fara sa derulezi inapoi.
   */
  rezumat() {
    const nod = document.createElement('section');
    nod.className = 'gravura__rezumat';
    nod.hidden = true;
    nod.setAttribute('aria-live', 'polite');
    const titlu = document.createElement('p');
    titlu.className = 'gravura__rezumat-titlu smallcaps';
    titlu.textContent = this.vorba('rezumat');
    this.rezumatLista = document.createElement('dl');
    this.rezumatLista.className = 'gravura__rezumat-lista';
    nod.append(titlu, this.rezumatLista);
    this.rezumatNod = nod;
    return nod;
  }

  /** Textul unui camp exact cum se graveaza: randurile alese de motor, cu „/" intre ele. */
  textulGravat(cod, scris) {
    const ultima = this.ultimeleAsezari?.get(cod)?.raspuns;
    const asezat = ultima && typeof ultima.text_asezat === 'string' ? ultima.text_asezat : null;
    const baza = asezat && asezat.replace(/\s+/g, ' ').trim() === scris.replace(/\s+/g, ' ').trim() ? asezat : scris;
    return baza.split('\n').map((rand) => rand.trim()).filter(Boolean).join(' / ');
  }

  actualizeazaRezumatul() {
    if (!this.rezumatNod) return;
    const randuri = [];
    for (const [cod, { camp, control }] of this.campuri ?? []) {
      if (this.estePoza(camp)) {
        if (this.stare.poza) randuri.push([this.eticheta(camp), this.vorba('rezumatPoza')]);
        continue;
      }
      const text = control.value.trim();
      if (text) randuri.push([this.eticheta(camp), `„${this.textulGravat(cod, text)}”`]);
    }
    const areCeva = randuri.length > 0;
    if (areCeva) {
      const font = (this.config?.fonturi ?? []).find((f) => String(f.id) === String(this.stare.font));
      if (font) randuri.push([this.vorba('pasFont'), font.eticheta]);
      if ((this.config?.impachetari ?? []).length > 0) {
        const cutie = this.impachetareaAleasa();
        const pret = cutie ? this.pretulCutiei({ pret: cutie.pret, moneda: cutie.moneda }) : this.vorba('gratis');
        randuri.push([
          this.vorba('pasCutie'),
          `${cutie ? cutie.eticheta : this.vorba('impachetareSimpla')}${pret ? ` · ${pret}` : ''}`,
        ]);
      }
      const total = this.totalCuCutia();
      if (total) randuri.push([this.vorba('total'), total]);
    }
    this.rezumatLista.replaceChildren(...randuri.map(([eticheta, valoare]) => {
      const rand = document.createElement('div');
      const dt = document.createElement('dt');
      const dd = document.createElement('dd');
      dt.textContent = eticheta;
      dd.textContent = valoare;
      rand.append(dt, dd);
      return rand;
    }));
    this.rezumatNod.hidden = !areCeva || this.stare.maiTarziu;
    this.actualizeazaPasii();
  }

  /**
   * Pasii facuti primesc bifa in locul numarului. Un formular lung se citeste
   * ca un drum: cat ai facut, cat mai e.
   */
  actualizeazaPasii() {
    if (!this.pasi) return;
    const textGata = [...(this.campuri?.values() ?? [])].some(({ camp, control }) => (
      this.estePoza(camp) ? Boolean(this.stare.poza) : Boolean(control.value.trim())
    )) && !this.motivulBlocarii();
    this.pasi.text?.classList.toggle('gravura__pas--gata', textGata);
    this.pasi.font?.classList.toggle('gravura__pas--gata', textGata && !this.motivFont);
    this.pasi.cutie?.classList.toggle('gravura__pas--gata', Boolean(this.stare.impachetare));
  }

  /**
   * Mostrele de font, cerute DUPA ce formularul e pe ecran.
   *
   * Sunt cateva zeci de kiloocteti de trasee; asteptate inainte de prima
   * randare, ar tine formularul gol degeaba. Cardurile se umplu cand sosesc.
   */
  async cereMostrele(text = null) {
    if (!this.cardFont || this.cardFont.size === 0) return;
    this.mostreCerute ??= new Map();
    const cheie = text ?? '';
    let raspuns = this.mostreCerute.get(cheie);
    if (!raspuns) {
      try {
        raspuns = await this.cere(`mostre?handle=${encodeURIComponent(this.dataset.handle)}`
          + (text ? `&text=${encodeURIComponent(text)}` : ''));
      } catch { return; }
      this.mostreCerute.set(cheie, raspuns);
      // Memorie mica: o sesiune scrie cateva texte, nu sute.
      if (this.mostreCerute.size > 24) this.mostreCerute.delete(this.mostreCerute.keys().next().value);
    }
    // Intre timp omul a scris altceva: mostrele vechi nu calca peste cele noi.
    if ((this.textulMostrelor() ?? '') !== cheie) return;

    for (const mostra of raspuns.mostre ?? []) {
      const gazda = this.cardFont.get(String(mostra.id))?.querySelector('[data-rol="mostra"]');
      if (!gazda || !mostra.viewBox || (mostra.cai ?? []).length === 0) continue;
      const svg = elem('svg', {
        class: 'gravura__mostra-svg',
        viewBox: mostra.viewBox,
        preserveAspectRatio: 'xMidYMid meet',
        'aria-hidden': 'true',
      });
      // Traseele sunt in spatiul fontului (y in SUS): un singur grup rastoarna.
      const grup = elem('g', { transform: 'scale(1 -1)' });
      grup.append(...(mostra.cai ?? []).map(caleaDin).filter(Boolean));
      svg.append(grup);
      gazda.replaceChildren(svg);
    }
  }

  /**
   * Textul cu care se scriu mostrele: ce a tastat omul in PRIMA casuta cu text,
   * primul rand. Fontul se alege pe numele lui, nu pe „Ana".
   */
  textulMostrelor() {
    for (const { camp, control } of this.campuri?.values() ?? []) {
      if (this.estePoza(camp)) continue;
      const rand = String(control.value ?? '').split('\n')[0].trim();
      if (rand) return rand;
    }
    return null;
  }

  /** Mostrele se cer cand omul se opreste din scris, nu la fiecare tasta. */
  programeazaMostrele() {
    clearTimeout(this.ceasMostre);
    this.ceasMostre = setTimeout(() => this.cereMostrele(this.textulMostrelor()), 700);
  }

  /**
   * Fata cu poza, desenata in plansa — aceeasi piesa, aceeasi zona, acelasi
   * `compune` ca la o fata cu text, doar ca in zona sta poza in loc de litere.
   *
   * Bucata desenata e EXACT cea din `stare.pozaCrop`, cu aceeasi geometrie ca
   * pe server (`poza-macheta.js`): un `<svg>` cuibarit cu `viewBox` pe bucata,
   * poza intreaga inauntru, rotita in jurul centrului bucatii, totul decupat pe
   * forma zonei. Asa, ce vede clientul aici, in machetă si pe piesa e acelasi
   * lucru — desenat de trei ori, niciodata altfel.
   *
   * Fara poza incarcata in sesiunea asta (dupa un refresh, pixelii nu mai sunt)
   * fasia ramane goala, cu vorba ei: mai bine gol decat o poza gresita.
   */
  deseneazaFataCuPoza() {
    const camp = (this.config?.campuri ?? []).find((c) => this.estePoza(c) && c.zona);
    if (!camp) return;
    const scena = this.scene?.get(camp.cod);
    if (!scena) return;

    const local = this.pozaLocala;
    const crop = this.stare.pozaCrop;
    if (!local?.url || !crop?.w) { this.golesteScena(camp.cod); return; }

    const svg = compune(
      { zona: camp.zona, piesa: camp.piesa ?? null, cai: [] },
      1,
      { macheta: this.aratamMacheta(), zoom: this.zoom },
    );

    // Zona in milimetri, exact cum o calculeaza `compune`.
    const zona = camp.zona;
    const piesa = camp.piesa ?? {};
    const latimePiesa = Number(piesa.latimeMm) || Number(zona.latimeMm) || 40;
    const inaltimePiesa = Number(piesa.inaltimeMm) || Number(zona.inaltimeMm) || 8;
    const latimeZona = Number(zona.latimeMm) || latimePiesa;
    const inaltimeZona = Number(zona.inaltimeMm) || inaltimePiesa;
    const zonaX = Number.isFinite(zona.offsetXMm) ? zona.offsetXMm : (latimePiesa - latimeZona) / 2;
    const zonaY = Number.isFinite(zona.offsetYMm) ? zona.offsetYMm : (inaltimePiesa - inaltimeZona) / 2;

    /*
     * Forma decupajului se deseneaza AICI, fara clasa `gravura__zona`.
     *
     * Refolosind `conturZonei`, forma venea cu clasa aia — iar in modul machetă
     * `gravura.css` o pune pe `display: none` (cadrele tehnice dispar). Un copil
     * ascuns nu contribuie la `clipPath`, deci decupajul iesea GOL si poza se
     * taia la nimic: fasia „Față" ramanea neagra, cu poza desenata si invizibila.
     */
    const idDecupaj = `gravura-poza-${camp.cod}`.replace(/[^a-z0-9_-]/gi, '');
    const defs = elem('defs', {});
    const decupaj = elem('clipPath', { id: idDecupaj });
    if (zona.forma === 'cerc') {
      decupaj.append(elem('circle', {
        cx: zonaX + latimeZona / 2, cy: zonaY + inaltimeZona / 2, r: Math.min(latimeZona, inaltimeZona) / 2,
      }));
    } else {
      decupaj.append(elem('rect', {
        x: zonaX, y: zonaY, width: latimeZona, height: inaltimeZona,
        rx: Math.min(Number(zona.razaMm) || 0, inaltimeZona / 2),
      }));
    }
    defs.append(decupaj);
    svg.append(defs);

    const grup = elem('g', { 'clip-path': `url(#${idDecupaj})` });
    const cuibar = elem('svg', {
      x: zonaX, y: zonaY, width: latimeZona, height: inaltimeZona,
      viewBox: `${crop.x} ${crop.y} ${crop.w} ${crop.h}`,
      preserveAspectRatio: 'xMidYMid slice',
    });
    const imagine = elem('image', {
      x: 0, y: 0, width: local.latime, height: local.inaltime,
      preserveAspectRatio: 'none', href: local.url,
    });
    if (crop.unghi) {
      const rotit = elem('g', {
        transform: `rotate(${crop.unghi} ${(crop.x + crop.w / 2).toFixed(1)} ${(crop.y + crop.h / 2).toFixed(1)})`,
      });
      rotit.append(imagine);
      cuibar.append(rotit);
    } else {
      cuibar.append(imagine);
    }
    grup.append(cuibar);
    svg.append(grup);

    this.deseneazaScena(camp.cod, svg);
    const plansaPx = Number(zona.plansaPx);
    if (plansaPx > 0) scena.style.setProperty('--gravura-plansa', `${plansaPx}px`);
  }

  /** Fasia fetei la care se lucreaza acum. `null` le stinge pe toate. */
  aprindeFasia(cod) {
    this.fasii?.querySelectorAll('[data-fasie]').forEach((fasie) => {
      fasie.classList.toggle('gravura__fata--activa', fasie.dataset.fasie === cod);
    });
  }

  /**
   * Scena fara text: BIJUTERIA, nu un dreptunghi gol.
   *
   * Pana pe 26 sep 2026 aici scria „Scrie textul de gravat", iar piesa aparea
   * abia dupa prima tasta. Acum piesa se deseneaza din prima (zona vine din
   * `/config`), cu un exemplu estompat de gravura pe ea — clientul vede pe ce
   * scrie inainte sa scrie. Exemplul e marcat ca exemplu si nu ajunge nicaieri:
   * nici in vitrina, nici in macheta marita, nici in cos.
   */
  golesteScena(cod) {
    const umplere = this.umpleri?.get(cod);
    if (umplere) umplere.hidden = true;
    this.ultimeleAsezari?.delete(cod);
    const scena = this.scene?.get(cod);
    if (!scena) return;
    const camp = (this.config?.campuri ?? []).find((c) => c.cod === cod) ?? null;
    const poza = camp ? this.estePoza(camp) : false;

    const vorba = document.createElement('p');
    vorba.className = 'gravura__gol text-xs';
    vorba.textContent = this.vorba(poza ? 'golPoza' : 'gol') || this.vorba('gol');

    scena.dataset.gol = '1';
    if (camp?.zona) {
      const svg = compune({ zona: camp.zona, piesa: camp.piesa ?? null, cai: [] }, 1, {
        macheta: this.aratamMacheta(), zoom: this.zoom,
      });
      svg.classList.add('gravura__svg--gol');
      scena.replaceChildren(svg, vorba);
      this.puneRaportul(scena, svg);
      if (!poza) this.cereExemplul(cod, camp);
    } else {
      scena.replaceChildren(vorba);
      this.indemnulUnic();
    }
    this.actualizeazaUnelteleScenei();
    // Sters textul, dispare si din vitrina: altfel poza produsului ar ramane
    // acoperita de o machetă a unui text care nu mai exista.
    this.actualizeazaOglinda();
  }

  /**
   * Textul exemplului: ce se cere des pe piesa asta (sugestiile sablonului,
   * apoi ideile), taiat la cat incape. „Ana" doar cand nu stim nimic.
   */
  textulExemplului(camp) {
    const limita = Number(camp?.max_caractere_auto ?? camp?.max_caractere) || 24;
    const candidati = [
      ...(this.config?.sugestii ?? []),
      ...(this.config?.idei ?? []).flatMap((grup) => grup.texte ?? []),
    ].filter((text) => typeof text === 'string' && text.trim() && [...text].length <= limita);
    return candidati[0] ?? 'Ana';
  }

  /** Exemplul estompat de pe o scena goala. Se cere o data per fata si font. */
  async cereExemplul(cod, camp) {
    this.exemple ??= new Map();
    const text = this.textulExemplului(camp);
    const cheie = `${cod}|${this.stare.font}|${text}`;
    let raspuns = this.exemple.get(cheie);
    if (!raspuns) {
      try {
        raspuns = await this.cere('asezare', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            handle: this.dataset.handle, parte: camp.parte, text, font: this.stare.font, asezare: ASEZARE_IMPLICITA,
          }),
        });
      } catch { return; }
      this.exemple.set(cheie, raspuns);
    }
    // Intre timp omul poate sa fi scris: exemplul nu calca peste textul lui.
    const scena = this.scene?.get(cod);
    if (!scena || scena.dataset.gol !== '1' || (this.stare.texte[cod] ?? '').trim()) return;
    if (!Array.isArray(raspuns?.cai) || raspuns.cai.length === 0) return;
    const svg = compune(raspuns, 1, { macheta: this.aratamMacheta(), zoom: this.zoom });
    svg.classList.add('gravura__svg--gol', 'gravura__svg--exemplu');
    const vorba = scena.querySelector('.gravura__gol');
    scena.replaceChildren(svg, ...(vorba ? [vorba] : []));
    this.puneRaportul(scena, svg);
    this.actualizeazaUnelteleScenei();
  }

  /** Scena primeste forma desenului: `aspect-ratio` din rama, nu o inaltime fixa. */
  puneRaportul(scena, svg) {
    const raport = Number(svg?.getAttribute?.('data-raport'));
    if (raport > 0) scena.style.setProperty('--raport', String(raport));
    // Fiecare desen isi poarta metalul FETEI lui: pe o piesa bicolora fata si
    // spatele au culori diferite, iar clonele (vitrina, fereastra marita) il
    // iau cu ele.
    svg?.setAttribute?.('data-metal', this.metalulFetei(scena.dataset.cod));
    this.indemnulUnic();
  }

  /** Metalul fetei `cod`, dupa varianta aleasa acum. */
  metalulFetei(cod) {
    const camp = (this.config?.campuri ?? []).find((c) => c.cod === cod);
    return metalulPartii(this.metaleleVariantei(), camp?.parte ?? 'fata') ?? 'aur';
  }

  /** Metalele variantei alese, in ordinea din titlu; rezerva, titlul produsului. */
  metaleleVariantei() {
    const id = String(this.varianta() ?? '');
    const varianta = this.variantele().find((v) => String(v.id) === id);
    const dinVarianta = metaleDin(varianta?.t);
    if (dinVarianta.length > 0) return dinVarianta;
    const dinProdus = metaleDin(this.config?.produs?.titlu);
    return dinProdus.length > 0 ? dinProdus : ['aur'];
  }

  /**
   * Indemnul „scrie textul mai jos" apare o singura data: pe PRIMA fata goala.
   * Pe o bratara fata-spate, doua fraze identice una sub alta se citesc ca o
   * greseala de pagina.
   */
  indemnulUnic() {
    let primul = true;
    for (const scena of this.scene?.values() ?? []) {
      const goala = scena.dataset.gol === '1';
      scena.classList.toggle('gravura__scena--fara-indemn', goala && !primul);
      if (goala) primul = false;
    }
  }

  /** Desenul nou al unei fete, cu textul clientului. */
  deseneazaScena(cod, svg) {
    const scena = this.scene?.get(cod);
    if (!scena) return;
    delete scena.dataset.gol;
    scena.classList.remove('gravura__scena--fara-indemn');
    svg.classList.add('gravura__svg--nou');
    scena.replaceChildren(svg);
    this.puneRaportul(scena, svg);
    this.actualizeazaUnelteleScenei();
    this.actualizeazaOglinda();
  }

  /** Zoom-ul s-a schimbat: fiecare fata se redeseneaza din ce stie deja, fara retea. */
  redeseneazaScenele() {
    for (const cod of this.scene?.keys() ?? []) {
      const ultima = this.ultimeleAsezari?.get(cod);
      const camp = (this.config?.campuri ?? []).find((c) => c.cod === cod);
      if (camp && this.estePoza(camp)) continue;
      if (ultima) {
        this.deseneazaScena(cod, compune(ultima.raspuns, ultima.randuri, { macheta: this.aratamMacheta(), zoom: this.zoom }));
      } else {
        this.golesteScena(cod);
      }
    }
    this.deseneazaFataCuPoza();
  }

  /**
   * Uneltele scenei: zoom pe gravura si „vezi mare".
   *
   * Zoom-ul apare doar cand are ce arata in plus (piesa e mult mai mare decat
   * locul unde se scrie — cruci, pandantive); pe o bara deja decupata ar fi
   * acelasi desen, iar un buton care nu schimba nimic invata omul sa nu apese.
   */
  actualizeazaUnelteleScenei() {
    if (!this.nasturZoom) return;
    const poate = [...(this.scene?.values() ?? [])].some((scena) => scena.querySelector('svg[data-poate-zoom]'));
    this.nasturZoom.hidden = !poate;
    this.nasturZoom.setAttribute('aria-pressed', String(Boolean(this.zoom)));
    this.nasturZoom.querySelector('[data-rol="zoom-text"]').textContent = this.vorba(this.zoom ? 'zoomInapoi' : 'zoomPe');
  }

  /** Nasturii din coltul scenei. Fara JS nu exista — si nici n-ar avea ce face. */
  uneltelePlansei(gazda) {
    if (!gazda) return;
    const nasture = (clasa, text, desen) => {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = `gravura__scena-nasture ${clasa}`;
      const svg = elem('svg', { viewBox: '0 0 24 24', 'aria-hidden': 'true', focusable: 'false' });
      for (const d of desen) svg.append(elem('path', { d }));
      const eticheta = document.createElement('span');
      eticheta.dataset.rol = 'zoom-text';
      eticheta.textContent = text;
      b.append(svg, eticheta);
      return b;
    };

    this.nasturZoom = nasture('gravura__scena-nasture--zoom', this.vorba('zoomPe'),
      ['M11 4a7 7 0 1 1 0 14 7 7 0 0 1 0-14z', 'M20 20l-4-4', 'M8 11h6', 'M11 8v6']);
    this.nasturZoom.hidden = true;
    this.nasturZoom.setAttribute('aria-pressed', 'false');
    this.nasturZoom.addEventListener('click', () => {
      this.zoom = !this.zoom;
      this.redeseneazaScenele();
    }, { signal: this.semnal });

    const mare = nasture('gravura__scena-nasture--mare', this.vorba('ecranMare'),
      ['M4 9V4h5', 'M20 9V4h-5', 'M4 15v5h5', 'M20 15v5h-5']);
    mare.querySelector('[data-rol="zoom-text"]').removeAttribute('data-rol');
    mare.addEventListener('click', () => this.arataMacheta(), { signal: this.semnal });

    gazda.append(this.nasturZoom, mare);
  }

  /** Variantele produsului (id + titlu), scrise de Liquid. */
  variantele() {
    if (this._variante !== undefined) return this._variante;
    try {
      const bloc = this.querySelector('[data-rol="variante-gravura"]');
      this._variante = bloc ? JSON.parse(bloc.textContent) : [];
    } catch { this._variante = []; }
    return this._variante;
  }

  /**
   * Metalul piesei, dupa varianta aleasa acum. Se reciteste la fiecare
   * schimbare din pagina: tema muta varianta fara sa ne anunte pe noi.
   */
  actualizeazaMetalul() {
    const metale = this.metaleleVariantei();
    // Pe formular: metalul fetei (primul) — rezerva pentru ce nu e un desen.
    if (this.dataset.metal !== metale[0]) this.dataset.metal = metale[0];
    for (const [cod, scena] of this.scene ?? []) {
      scena.querySelector('svg.gravura__svg')?.setAttribute('data-metal', this.metalulFetei(cod));
    }
    // Desenele din vitrina sunt clone: se refac cu metalul nou.
    this.actualizeazaOglinda();
  }

  mareLaInceput(cuvant) {
    const c = String(cuvant || 'fata');
    return c.charAt(0).toUpperCase() + c.slice(1);
  }

  // `fel` vine de pe campul de metaobject, dar exemplul din §4 il omite; se cade inapoi pe cod.
  // Un camp de poza tratat ca text ar cere clientului sa scrie o fotografie.
  estePoza(camp) { return camp.fel === 'poza' || camp.cod === 'poza_ochi'; }

  eticheta(camp) {
    const etichete = camp.eticheta ?? {};
    return etichete[this.dataset.limba] ?? etichete.ro ?? camp.cod;
  }

  campText(camp) {
    const nod = this.cloneaza('camp-text');
    const id = this.identificator(camp.cod);
    const eticheta = nod.querySelector('[data-rol="eticheta"]');
    eticheta.textContent = this.eticheta(camp);
    eticheta.setAttribute('for', id);

    // `randuri` spune cat loc de scris se arata, nu cat incape: limita reala o dau zona si
    // fontul, si vine cu fiecare raspuns de asezare.
    const multe = Number(camp.randuri ?? 1) > 1;
    const control = document.createElement(multe ? 'textarea' : 'input');
    control.className = multe ? 'textarea' : 'input';
    control.id = id;
    control.maxLength = MAX_CARACTERE;
    control.autocomplete = 'off';
    control.spellcheck = false;
    if (multe) control.rows = Number(camp.randuri);
    control.value = this.stare.texte[camp.cod] ?? '';
    // Cand scrii intr-un camp, fasia LUI se aprinde: pe o piesa cu doua fete,
    // altfel te uiti la desenul celeilalte fara sa-ti dai seama.
    control.addEventListener('focus', () => this.aprindeFasia(camp.cod), { signal: this.semnal });
    control.addEventListener('blur', () => this.aprindeFasia(null), { signal: this.semnal });
    control.addEventListener('input', () => {
      // Selectorul de emoji (FE0F) vine din tastaturile de telefon lipit de
      // inima. Pe metal nu exista „stil de emoji" — se scoate din mers, cu
      // cursorul pastrat, inainte sa ajunga in sha-ul textului.
      if (control.value.includes('\uFE0F')) {
        const pozitie = control.selectionStart ?? control.value.length;
        const inainte = control.value.slice(0, pozitie).replace(/\uFE0F/g, '');
        control.value = control.value.replace(/\uFE0F/g, '');
        control.setSelectionRange(inainte.length, inainte.length);
      }
      this.stare.texte[camp.cod] = control.value;
      this.schimbat();
      this.programeazaAsezare(camp.cod);
    }, { signal: this.semnal });
    nod.querySelector('[data-rol="control"]').append(control);

    this.simboluri(nod.querySelector('[data-rol="simboluri"]'), control, camp.cod);
    if ((this.config.sugestii ?? []).length > 0 && !this.sugestiiPuse) {
      this.sugestiiPuse = true;
      nod.append(this.sugestiileSablonului(control));
    }

    // Ideile de gravura: pe FIECARE camp de text, fiindca fiecare fata e alta
    // intrebare („ce scriu pe fata?" nu e „ce scriu pe spate?").
    //
    // Butonul sta LANGA caseta, nu sub ea: pe o piesa cu doua fete, doua
    // butoane pe randuri proprii adaugau doua randuri de derulat inainte de
    // fonturi. Perechea caseta+buton se leaga cu o clasa pe camp, iar CSS-ul
    // le pune pe acelasi rand (70/30) cand e loc.
    if ((this.config.idei ?? []).length > 0) {
      nod.classList.add('gravura__camp--cu-idei');
      const control2 = nod.querySelector('[data-rol="control"]') ?? control;
      const invelis = document.createElement('div');
      invelis.className = 'gravura__rand-scriere';
      control2.parentNode.insertBefore(invelis, control2);
      invelis.append(control2, this.nasturulDeIdei(control));
    }

    // Cat incape se spune INAINTE de prima tasta. Cifra din config e calculata cu fontul
    // implicit — si cu ruperea pe doua randuri, cand serverul o stie; la prima asezare o
    // inlocuieste una care stie si textul, si fontul ales.
    const cota = [];
    const incap = Number.isFinite(camp.max_caractere_auto) ? camp.max_caractere_auto : camp.max_caractere;
    if (Number.isFinite(incap)) cota.push(this.vorba('ramase', { n: incap }));
    nod.querySelector('[data-rol="cota"]').textContent = cota.join(' · ');

    this.campuri.set(camp.cod, { camp, nod, control });
    // Regula randurilor se spune de la inceput, nu abia dupa ce se rupe textul.
    this.scrieRandurile(camp.cod, null);
    if (control.value) this.programeazaAsezare(camp.cod, 0);
    return nod;
  }

  /**
   * Cum se rup randurile: MEREU automat — motorul alege ruperea cu litera cea
   * mai mare. Comutatorul „Automat / 1 rand / 2 randuri" a fost scos pe 26 sep
   * 2026 (cerut de Vlad): o alegere in plus pe o pagina de produs e o intrebare
   * in plus, iar raspunsul bun il stie oricum motorul.
   */
  asezareaCampului() {
    return ASEZARE_IMPLICITA;
  }

  /**
   * Nota de sub caseta, cand motorul a rupt textul pe doua randuri: spune de
   * ce, in milimetri. Fara ea, clientul ar vedea randul rupt si ar crede ca e
   * o greseala de asezare.
   */
  scrieRandurile(cod, raspuns) {
    const intrare = this.campuri.get(cod);
    const nota = intrare?.nod.querySelector('[data-rol="randuri-nota"]');
    if (!nota) return;
    // Regula e dupa numarul de caractere, deci se poate SPUNE dinainte:
    // „peste 14 semne, textul trece pe randul al doilea". Dupa rupere, o
    // confirmare scurta. Fara milimetri — ii spun omului ce se intampla, nu il
    // pun sa faca socoteli.
    const prag = Number(raspuns?.prag ?? intrare.camp?.rand_nou_dupa);
    // Regula se spune doar cand se poate atinge: pe o cruce unde pragul e ~35
    // si incap ~14 semne, „peste 35 trece pe randul doi" n-ar avea obiect.
    const incap = Number(intrare.camp?.max_caractere_auto ?? intrare.camp?.max_caractere);
    const seAtinge = Number.isFinite(prag) && prag > 0 && (!Number.isFinite(incap) || prag < incap);
    let text = '';
    if (raspuns?.rupt_automat) text = this.vorba('randuriRupt', { n: Number.isFinite(prag) ? prag : '' });
    else if (seAtinge) text = this.vorba('randuriPrag', { n: prag });
    nota.textContent = text;
    nota.hidden = !text;
  }

  /** Milimetrii scrisi in limba paginii: „5,2" in romana, „5.2" in engleza. */
  mm(valoare) {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'ro', {
        minimumFractionDigits: 1, maximumFractionDigits: 1,
      }).format(valoare);
    } catch { return Number(valoare).toFixed(1); }
  }

  /**
   * Semnul de langa eticheta: „Gravura buna" — si doar la un text chiar prea
   * lung, „scurteaza-l".
   *
   * Prima versiune avea patru trepte cu milimetri („Litere mici · 2,0 mm"), iar
   * Vlad a oprit-o pe 26 sep 2026: o eticheta ingrijoratoare pe un text care se
   * graveaza bine il face pe client sa ezite fara motiv. Acum e liniste pana la
   * pragul unde chiar are ce corecta — litera sub 2 mm — si abia atunci i se
   * spune ce sa faca. Milimetrii raman in `title`, pentru cine vrea sa stie.
   */
  scrieLizibilitatea(cod, raspuns) {
    const loc = this.campuri.get(cod)?.nod.querySelector('[data-rol="lizibil"]');
    if (!loc) return;
    const mm = Number(raspuns?.inaltime_litera_mm);
    if (!Number.isFinite(mm) || mm <= 0) { loc.hidden = true; return; }
    // „Prea lung" cere AMBELE: litera sub 2 mm si un text chiar lung (peste
    // 24 de semne). Un text de 17 semne, trecut pe doua randuri pe o cruce,
    // ramane „Gravura buna" — asa l-a vrut Vlad.
    const semne = [...String(this.campuri.get(cod)?.control.value ?? '').trim()].length;
    const lung = mm < LITERA_MINIMA_LINISTITA_MM && semne > TEXT_CHIAR_LUNG;
    loc.dataset.treapta = lung ? 'lung' : 'bun';
    loc.textContent = this.vorba(lung ? 'lizibilLung' : 'lizibilBun');
    loc.title = `${this.vorba('lizibilTitlu')}: ${this.vorba('literaMm', { mm: this.mm(mm) })}`;
    loc.hidden = false;
  }

  simboluri(gazda, control, cod) {
    const lista = this.config.simboluri ?? [];
    if (lista.length === 0) { gazda.remove(); return; }
    gazda.setAttribute('aria-label', this.vorba('simboluri'));

    for (const simbol of lista) {
      const nasture = this.cloneaza('simbol');
      // Desenul NOSTRU, nu glifa aceluiasi caracter din fontul telefonului:
      // altfel clientul alege o cruce si primeste gravata alta.
      nasture.replaceChildren(this.desenulSimbolului(simbol));
      nasture.title = simbol.eticheta ?? simbol.car;
      nasture.setAttribute('aria-label', simbol.eticheta ?? simbol.car);
      nasture.dataset.car = simbol.car;
      nasture.addEventListener('click', () => this.apasaSimbolul(control, simbol.car), { signal: this.semnal });
      gazda.append(nasture);
    }

    // Data e a doua gravura ca frecventa dupa nume. Chipul deschide calendarul
    // si scrie data gata formatata (16.08.2026), la cursor.
    const data = document.createElement('input');
    data.type = 'date';
    data.className = 'gravura__data-ascunsa';
    data.tabIndex = -1;
    data.setAttribute('aria-hidden', 'true');
    const chipData = this.cloneaza('simbol');
    chipData.replaceChildren(this.pictogramaCalendar());
    chipData.classList.add('gravura__simbol--unealta');
    chipData.title = this.vorba('dataChip');
    chipData.setAttribute('aria-label', this.vorba('dataChip'));
    chipData.addEventListener('click', () => {
      try { data.showPicker(); } catch { data.focus(); data.click(); }
    }, { signal: this.semnal });

    // Pe telefon, `showPicker()` fie nu exista (iOS mai vechi), fie refuza un
    // input nefocusabil — chipul parea mort. Inputul de data sta acum PESTE
    // chip, transparent: atingerea cade chiar pe el si selectorul nativ se
    // deschide singur. Pe desktop, clicul pe input cere si el selectorul.
    const invelisData = document.createElement('span');
    invelisData.className = 'gravura__data-invelis';
    chipData.replaceWith(invelisData);
    invelisData.append(chipData, data);
    data.tabIndex = -1;
    data.addEventListener('click', () => {
      try { data.showPicker(); } catch { /* nativul isi face treaba singur */ }
    }, { signal: this.semnal });
    data.addEventListener('change', () => {
      if (!data.value) return;
      const [an, luna, zi] = data.value.split('-');
      const frumos = `${zi}.${luna}.${an}`;
      const start = control.selectionStart ?? control.value.length;
      const stop = control.selectionEnd ?? start;
      control.value = control.value.slice(0, start) + frumos + control.value.slice(stop);
      control.setSelectionRange(start + frumos.length, start + frumos.length);
      control.focus();
      control.dispatchEvent(new Event('input', { bubbles: true }));
    }, { signal: this.semnal });
    gazda.append(invelisData);

    this.marcheazaSimbolActiv(gazda, control);
    control.addEventListener('input', () => this.marcheazaSimbolActiv(gazda, control), { signal: this.semnal });
  }

  /** Simbolul, ca desen, gata de pus intr-un nasture. */
  desenulSimbolului(simbol) {
    const vedere = simbol.vedere ?? {};
    const svg = elem('svg', {
      class: 'gravura__simbol-svg',
      viewBox: vedere.viewBox || '0 0 100 100',
      preserveAspectRatio: 'xMidYMid meet',
      'aria-hidden': 'true',
      focusable: 'false',
    });
    const cale = elem('path', vedere.transform
      ? { d: vedere.d ?? '', transform: vedere.transform }
      : { d: vedere.d ?? '' });
    svg.append(cale);
    return svg;
  }

  /** Calendarul, desenat: un emoji arata altfel pe fiecare sistem si nu se coloreaza. */
  pictogramaCalendar() {
    const svg = elem('svg', {
      class: 'gravura__unealta-svg', viewBox: '0 0 24 24',
      'aria-hidden': 'true', focusable: 'false',
    });
    svg.append(
      elem('rect', { x: 3, y: 5, width: 18, height: 16, rx: 2 }),
      elem('path', { d: 'M3 10h18M8 3v4M16 3v4' }),
    );
    return svg;
  }

  /** Sirul-tipar care gaseste simbolul din text, cu tot cu scrierile vechi. */
  tiparSimbol() {
    const cars = (this.config.simboluri ?? []).map((simbol) => simbol.car);
    const bucati = cars.map((car) => car.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
    if (cars.includes('♥')) bucati.push('❤', '<3');
    return bucati.length > 0 ? new RegExp(bucati.join('|'), 'u') : null;
  }

  /**
   * Butonul de simbol: piesa poarta UN simbol. Daca textul are deja unul,
   * clicul pe altul il INLOCUIESTE (pozitia ramane a lui), clicul pe al lui
   * il scoate. Fara niciun simbol in text, intra la cursor — clientul alege
   * UNDE cade inima.
   */
  apasaSimbolul(control, car) {
    const tipar = this.tiparSimbol();
    const gasit = tipar ? control.value.match(tipar) : null;

    if (gasit) {
      const este = gasit[0] === car || (car === '♥' && (gasit[0] === '❤' || gasit[0] === '<3'));
      const inceput = gasit.index;
      if (este) {
        // Scoaterea repara si spatiile: „Ana ♥ Ion" -> „Ana Ion", nu „Ana  Ion".
        control.value = (control.value.slice(0, inceput) + control.value.slice(inceput + gasit[0].length))
          .replace(/  +/g, ' ');
        control.setSelectionRange(inceput, inceput);
      } else {
        control.value = control.value.slice(0, inceput) + car + control.value.slice(inceput + gasit[0].length);
        control.setSelectionRange(inceput + car.length, inceput + car.length);
      }
    } else {
      const start = control.selectionStart ?? control.value.length;
      const stop = control.selectionEnd ?? start;
      const inainte = control.value.slice(0, start);
      const dupa = control.value.slice(stop);

      // Aerul din jurul simbolului: fara el iese „Ana ♥Ion", care pe metal
      // arata a greseala de tastare — si atunci e prea tarziu.
      const cereAerStanga = inainte.length > 0 && !/\s$/.test(inainte);
      const cereAerDreapta = dupa.length > 0 && !/^\s/.test(dupa);
      const bucata = (cereAerStanga ? ' ' : '') + car + (cereAerDreapta ? ' ' : '');

      control.value = inainte + bucata + dupa;
      const cursor = inainte.length + bucata.length;
      control.setSelectionRange(cursor, cursor);
    }
    control.focus();
    control.dispatchEvent(new Event('input', { bubbles: true }));
  }

  /** Butonul simbolului prezent in text se aprinde — starea se vede, nu se ghiceste. */
  marcheazaSimbolActiv(gazda, control) {
    const tipar = this.tiparSimbol();
    const gasit = tipar ? control.value.match(tipar) : null;
    const activ = gasit ? (gasit[0] === '❤' || gasit[0] === '<3' ? '♥' : gasit[0]) : null;
    gazda.querySelectorAll('[data-car]').forEach((nasture) => {
      nasture.classList.toggle('gravura__simbol--activ', nasture.dataset.car === activ);
    });
  }

  /** Click pe simbolul DIN DESEN: se schimba cu urmatorul din lista. */
  schimbaSimbolul(desen) {
    const scena = desen.closest('[data-cod]');
    const intrare = scena ? this.campuri.get(scena.dataset.cod) : null;
    const lista = (this.config.simboluri ?? []).map((simbol) => simbol.car);
    if (!intrare || lista.length === 0) return;

    const acum = desen.dataset.simbol === '❤' || desen.dataset.simbol === '<3' ? '♥' : desen.dataset.simbol;
    const urmatorul = lista[(lista.indexOf(acum) + 1) % lista.length] ?? lista[0];
    this.apasaSimbolul(intrare.control, urmatorul);
  }

  /**
   * „Idei de gravură" — raspunsul la caseta goala.
   *
   * Cel mai greu moment al formularului nu e tehnic: clientul stie ca vrea o
   * gravura si nu stie CE sa scrie. Panoul se deschide dintr-un buton (nu sta
   * mereu deschis: ar ingropa campul), iar ideile vin deja filtrate de server
   * dupa cui e piesa si taiate la cate caractere incap.
   */
  nasturulDeIdei(control) {
    const gazda = document.createElement('div');
    gazda.className = 'gravura__idei';

    const nasture = document.createElement('button');
    nasture.type = 'button';
    nasture.className = 'gravura__idei-buton text-xs';
    nasture.textContent = this.vorba('idei');
    nasture.setAttribute('aria-expanded', 'false');

    const panou = document.createElement('div');
    panou.className = 'gravura__idei-panou';
    panou.hidden = true;

    for (const grup of this.config.idei ?? []) {
      const titlu = document.createElement('p');
      titlu.className = 'gravura__idei-grup smallcaps';
      titlu.textContent = grup.nume;
      panou.append(titlu);

      const rand = document.createElement('div');
      rand.className = 'gravura__idei-lista';
      for (const text of grup.texte) {
        const chip = document.createElement('button');
        chip.type = 'button';
        chip.className = 'gravura__sugestie text-xs';
        chip.textContent = text;
        chip.addEventListener('click', () => {
          control.value = text;
          control.focus();
          control.dispatchEvent(new Event('input', { bubbles: true }));
          // Panoul se inchide dupa alegere: omul a primit ce cauta, iar
          // urmatorul lucru de facut e sa vada textul pe piesa.
          panou.hidden = true;
          nasture.setAttribute('aria-expanded', 'false');
        }, { signal: this.semnal });
        rand.append(chip);
      }
      panou.append(rand);
    }

    nasture.addEventListener('click', () => {
      const inchis = panou.hidden;
      panou.hidden = !inchis;
      nasture.setAttribute('aria-expanded', String(inchis));
    }, { signal: this.semnal });

    gazda.append(nasture);
    // Panoul se agata de CAMP, nu de invelisul ingust: are nevoie de toata
    // latimea ca sa incapa chip-urile pe randuri, nu cate unul.
    this.paneleDeIdei = this.paneleDeIdei ?? [];
    this.paneleDeIdei.push(panou);
    gazda.dataset.panou = String(this.paneleDeIdei.length - 1);
    queueMicrotask(() => {
      const camp = gazda.closest('.gravura__camp');
      if (camp) camp.append(panou);
      else gazda.append(panou);
    });
    return gazda;
  }

  /** Sugestiile sablonului: texte care se cer des pe piesa asta, gata de pus. */
  sugestiileSablonului(control) {
    const gazda = document.createElement('div');
    gazda.className = 'gravura__sugestii';
    for (const text of (this.config.sugestii ?? []).slice(0, 6)) {
      const chip = document.createElement('button');
      chip.type = 'button';
      chip.className = 'gravura__sugestie text-xs';
      chip.textContent = text.length > 34 ? `${text.slice(0, 34)}…` : text;
      chip.title = text;
      chip.addEventListener('click', () => {
        control.value = text;
        control.focus();
        control.dispatchEvent(new Event('input', { bubbles: true }));
      }, { signal: this.semnal });
      gazda.append(chip);
    }
    return gazda;
  }

  campPoza(camp) {
    const nod = this.cloneaza('camp-poza');
    const id = this.identificator(camp.cod);
    const eticheta = nod.querySelector('[data-rol="eticheta"]');
    eticheta.textContent = this.eticheta(camp);
    eticheta.setAttribute('for', id);

    const fisier = nod.querySelector('[data-rol="fisier"]');
    fisier.id = id;
    this.pozaArata(nod, null);
    nod.querySelector('[data-rol="alege"]').addEventListener('click', () => fisier.click(), { signal: this.semnal });
    fisier.addEventListener('change', () => this.incarcaPoza(nod, fisier.files?.[0]), { signal: this.semnal });
    nod.querySelector('[data-rol="scoate"]').addEventListener('click', () => {
      this.stare.poza = null;
      this.stare.pozaCrop = null;
      this.punPozaLocala(null);
      this.punMiniatura(null);
      this.inc = null;
      this.reincadrareNoua = false;
      this.inchideIncadrarea(nod);
      fisier.value = '';
      this.pozaArata(nod, null);
      this.deseneazaFataCuPoza();
      this.schimbat();
    }, { signal: this.semnal });
    nod.querySelector('[data-rol="reincadreaza"]').addEventListener('click', (ev) => {
      // Accentul si-a facut treaba: omul a aflat ca poate corecta incadrarea.
      this.reincadrareNoua = false;
      ev.currentTarget.classList.remove('gravura__reincadreaza--nou');
      this.deschideIncadrarea(nod);
    }, { signal: this.semnal });
    nod.querySelector('[data-rol="gata"]').addEventListener('click', () => {
      this.inchideIncadrarea(nod);
      this.pozaArata(nod, this.miniaturaUrl ?? null);
    }, { signal: this.semnal });
    this.leagaGesturile(nod);

    this.campuri.set(camp.cod, { camp, nod, control: fisier });
    return nod;
  }

  /** Campul de poza al retetei, cu zona lui — de acolo vine forma chenarului. */
  zonaPozei() {
    return (this.config?.campuri ?? []).find((camp) => this.estePoza(camp))?.zona ?? null;
  }

  /**
   * Forma chenarului: latime / inaltime, exact ca zona gravata.
   *
   * Fara zona masurata (produs nou, sablon lipsa) se ia forma barei obisnuite,
   * 36 x 15 — mai bine un chenar aproximativ decat mijlocul pozei.
   */
  aspectulPozei() {
    const zona = this.zonaPozei();
    if (zona?.forma === 'cerc') return 1;
    const l = Number(zona?.latimeMm);
    const i = Number(zona?.inaltimeMm);
    return l > 0 && i > 0 ? l / i : 36 / 15;
  }

  sfat(nod, text) {
    const loc = nod.querySelector('[data-rol="sfat"]');
    if (loc) loc.textContent = text ?? '';
  }

  /**
   * Poza, pregatita de incarcare: rotirea din EXIF coapta in pixeli si latura
   * lunga adusa la cel mult `POZA_LATURA_MAX`.
   *
   * De ce se reincodeaza: pozele de telefon poarta rotirea ca o eticheta EXIF,
   * pe care browserul o aplica la afisare, dar serverul (si laserul) o vad ca
   * pe pixelii bruti. Un chenar ales pe poza „in picioare" ar cadea pe alta
   * bucata din poza „culcata". Dupa canvas, pixelii sunt aceiasi peste tot.
   * Bonus: 900 KB in loc de 6 MB pe drum.
   */
  async pregatestePoza(fisier) {
    const adresa = URL.createObjectURL(fisier);
    try {
      const img = await imagineDin(adresa);
      const k = Math.min(1, POZA_LATURA_MAX / Math.max(img.naturalWidth, img.naturalHeight));
      const latime = Math.max(1, Math.round(img.naturalWidth * k));
      const inaltime = Math.max(1, Math.round(img.naturalHeight * k));
      const panza = document.createElement('canvas');
      panza.width = latime;
      panza.height = inaltime;
      panza.getContext('2d').drawImage(img, 0, 0, latime, inaltime);
      // PNG ramane PNG (poate avea transparenta); JPEG si WEBP pleaca JPEG.
      const tip = fisier.type === 'image/png' ? 'image/png' : 'image/jpeg';
      const blob = await new Promise((rezolva, respinge) => {
        panza.toBlob((b) => (b ? rezolva(b) : respinge(new Error('reincodarea a esuat'))), tip, 0.92);
      });
      return { blob, latime, inaltime, tip };
    } finally {
      URL.revokeObjectURL(adresa);
    }
  }

  /**
   * Chenarul, peste poza: se deschide dupa incarcare si la „Reincadreaza".
   *
   * Geometria se masoara abia cand editorul e la vedere; intr-un pas ascuns al
   * chestionarului latimea e 0 si se asteapta urmatorul cadru.
   */
  deschideIncadrarea(nod) {
    const local = this.pozaLocala;
    if (!local) return;
    const editor = nod.querySelector('[data-rol="incadrare"]');
    const img = nod.querySelector('[data-rol="incadrarepoza"]');
    const cadru = nod.querySelector('[data-rol="cadru"]');
    nod.querySelector('[data-rol="incadraretitlu"]').textContent = this.vorba('incadreaza');
    nod.querySelector('[data-rol="zoometicheta"]').textContent = this.vorba('incadrareMareste');
    nod.querySelector('[data-rol="rotireeticheta"]').textContent = this.vorba('incadrareRoteste');
    nod.querySelector('[data-rol="gata"]').textContent = this.vorba('incadrareGata');
    cadru.classList.toggle('gravura__cadru--cerc', this.zonaPozei()?.forma === 'cerc');
    if (img.getAttribute('src') !== local.url) img.src = local.url;
    editor.hidden = false;
    if (!this.inc) this.sfat(nod, this.vorba('incadrareSfat'));
    this.nodIncadrare = nod;
    requestAnimationFrame(() => this.asazaScena(nod));
  }

  inchideIncadrarea(nod) {
    const editor = nod.querySelector('[data-rol="incadrare"]');
    if (editor) editor.hidden = true;
    this.nodIncadrare = null;
  }

  /**
   * Scena si chenarul, in pixeli de ecran.
   *
   * Chenarul ia aproape toata latimea, are forma zonei si STA PE LOC; poza se
   * misca, se mareste si se roteste sub el, in jurul centrului chenarului.
   * Starea pozei e (cx, cy, s, unghi): ce punct al pozei sta in centrul
   * chenarului, cat de marita e si cu cat e rotita. Poza se pune la baza cu
   * latimea chenarului (scara 1 = poza cat chenarul), iar `k0` spune cati
   * pixeli de ecran inseamna un pixel de poza la scara 1.
   */
  asazaScena(nod) {
    const local = this.pozaLocala;
    const scena = nod.querySelector('[data-rol="scenapoza"]');
    if (!local || !scena || scena.clientWidth < 50) return;
    const aspect = this.aspectulPozei();
    const latimeScena = scena.clientWidth;
    const fW = Math.round(Math.min(latimeScena * 0.86, aspect < 1.3 ? 320 : latimeScena));
    const fH = Math.round(fW / aspect);
    const aer = Math.round(Math.max(22, Math.min(fH * 0.45, 90)));
    scena.style.height = `${fH + aer * 2}px`;
    const fx = Math.round((latimeScena - fW) / 2);
    const fy = aer;
    const cadru = nod.querySelector('[data-rol="cadru"]');
    Object.assign(cadru.style, { left: `${fx}px`, top: `${fy}px`, width: `${fW}px`, height: `${fH}px` });

    const fw = fW;
    const fh = fW * local.inaltime / local.latime;
    const img = nod.querySelector('[data-rol="incadrarepoza"]');
    img.style.width = `${fw}px`;
    img.style.height = `${fh}px`;

    const inc = this.inc ?? { atins: false, sursa: null, unghi: 0 };
    // Plafonul de marire: cel putin 5x, dar destul cat chenarul sa poata cobori
    // la 160 px de poza — o fata mica intr-o poza de grup. Masurat pe viu: la
    // 5x fix, chenarul se oprea exact pe o fata de marime medie si pe una mai
    // mica ar fi ramas prea larg, cu tot obrazul in el.
    Object.assign(inc, {
      fx, fy, fW, fH, fw, fh, k0: fw / local.latime, sMax: Math.max(5, local.latime / 160), s: inc.s ?? 1,
    });
    this.inc = inc;

    // Chenarul deja ales (reincadrare, sau latimea s-a schimbat) se pune la loc;
    // altfel poza se centreaza sub chenar, dreapta.
    if (this.stare.pozaCrop) {
      this.asazaCadrulPe(this.stare.pozaCrop);
    } else {
      inc.cx = fw / 2;
      inc.cy = fh / 2;
      inc.unghi = 0;
      inc.s = this.scaraMinima(0);
      this.aplicaIncadrarea();
      this.scrieIncadrarea('client');
    }
  }

  /**
   * Cea mai mica marire la care poza, rotita cu `unghi`, mai acopera chenarul.
   * Chenarul rotit invers in poza are jumatatile de latura (ex, ey); trebuie sa
   * incapa in poza pe amandoua directiile.
   */
  scaraMinima(unghi) {
    const inc = this.inc;
    const rad = (unghi * Math.PI) / 180;
    const cos = Math.abs(Math.cos(rad));
    const sin = Math.abs(Math.sin(rad));
    return Math.max((inc.fW * cos + inc.fH * sin) / inc.fw, (inc.fW * sin + inc.fH * cos) / inc.fh);
  }

  /** Pune chenarul pe un dreptunghi dat in pixelii pozei (cu unghiul lui). */
  asazaCadrulPe(dreptunghi) {
    const inc = this.inc;
    if (!inc || !dreptunghi?.w) return;
    inc.cx = (dreptunghi.x + dreptunghi.w / 2) * inc.k0;
    inc.cy = (dreptunghi.y + dreptunghi.h / 2) * inc.k0;
    inc.unghi = Math.max(-ROTIRE_MAX, Math.min(ROTIRE_MAX, Number(dreptunghi.unghi) || 0));
    inc.s = inc.fW / (dreptunghi.w * inc.k0);
    this.aplicaIncadrarea();
  }

  /**
   * Poza nu are voie sa lase chenarul descoperit: scara nu coboara sub cea
   * minima pentru unghiul curent, iar centrul ramane la cel putin jumatate de
   * chenar (rotit) de fiecare margine a pozei. Apoi se deseneaza: centrul pozei
   * ales vine in centrul chenarului, rotit si marit acolo.
   */
  aplicaIncadrarea() {
    const inc = this.inc;
    const nod = this.nodIncadrare;
    if (!inc || !nod) return;
    inc.s = Math.max(this.scaraMinima(inc.unghi), Math.min(inc.sMax, inc.s));
    const rad = (inc.unghi * Math.PI) / 180;
    const cos = Math.abs(Math.cos(rad));
    const sin = Math.abs(Math.sin(rad));
    const ex = (inc.fW * cos + inc.fH * sin) / (2 * inc.s);
    const ey = (inc.fW * sin + inc.fH * cos) / (2 * inc.s);
    inc.cx = Math.min(inc.fw - ex, Math.max(ex, inc.cx));
    inc.cy = Math.min(inc.fh - ey, Math.max(ey, inc.cy));

    const Fx = inc.fx + inc.fW / 2;
    const Fy = inc.fy + inc.fH / 2;
    nod.querySelector('[data-rol="incadrarepoza"]').style.transform =
      `translate(${Fx}px, ${Fy}px) rotate(${inc.unghi}deg) scale(${inc.s}) translate(${-inc.cx}px, ${-inc.cy}px)`;

    const zoom = nod.querySelector('[data-rol="zoom"]');
    const sMin = this.scaraMinima(inc.unghi);
    if (zoom) zoom.value = String(Math.round(100 * Math.log(inc.s / sMin) / Math.log(Math.max(1.0001, inc.sMax / sMin))));
    const rotire = nod.querySelector('[data-rol="rotire"]');
    if (rotire) rotire.value = String(inc.unghi);
  }

  /** Tras cu degetul pe ecran: centrul ales se muta invers, in pixelii pozei, tinand cont de rotire. */
  mutaIncadrarea(dx, dy) {
    const inc = this.inc;
    if (!inc) return;
    const rad = (inc.unghi * Math.PI) / 180;
    inc.cx -= (dx * Math.cos(rad) + dy * Math.sin(rad)) / inc.s;
    inc.cy -= (-dx * Math.sin(rad) + dy * Math.cos(rad)) / inc.s;
    inc.atins = true;
    this.aplicaIncadrarea();
  }

  /** Mareste in jurul centrului chenarului — ochii raman unde sunt. */
  maresteIncadrarea(sNou) {
    const inc = this.inc;
    if (!inc) return;
    inc.s = sNou;
    inc.atins = true;
    this.aplicaIncadrarea();
  }

  /** Roteste poza in jurul centrului chenarului, in grade, cu limita. */
  rotesteIncadrarea(unghi) {
    const inc = this.inc;
    if (!inc) return;
    inc.unghi = Math.max(-ROTIRE_MAX, Math.min(ROTIRE_MAX, Math.round(unghi * 10) / 10));
    inc.atins = true;
    this.aplicaIncadrarea();
  }

  /** Chenarul de pe ecran, tradus in pixelii pozei incarcate: centru, marime, unghi. */
  incadrareaCurenta() {
    const inc = this.inc;
    const local = this.pozaLocala;
    if (!inc || !local) return null;
    const w = Math.max(8, Math.round(inc.fW / inc.s / inc.k0));
    const h = Math.max(8, Math.round(inc.fH / inc.s / inc.k0));
    const cx = inc.cx / inc.k0;
    const cy = inc.cy / inc.k0;
    return { x: Math.round(cx - w / 2), y: Math.round(cy - h / 2), w, h, unghi: inc.unghi };
  }

  /**
   * Alegerea, in stare: de aici pleaca in validare si in jeton.
   *
   * `sursa` spune atelierului cine a asezat chenarul: 'auto' doar cat timp omul
   * nu l-a atins. Se scrie la sfarsitul gestului, nu la fiecare pixel — fiecare
   * scriere reface macheta pe server.
   */
  scrieIncadrarea(sursa) {
    const dreptunghi = this.incadrareaCurenta();
    const local = this.pozaLocala;
    if (!dreptunghi || !local) return;
    this.stare.pozaCrop = {
      ...dreptunghi,
      latime: local.latime,
      inaltime: local.inaltime,
      sursa: sursa === 'auto' && !this.inc?.atins ? 'auto' : 'client',
    };
    this.miniaturaDecupata();
    // Plansa se reface pe loc: chenarul se misca sub deget si piesa din desen
    // se schimba odata cu el.
    this.deseneazaFataCuPoza();
    this.schimbat();
  }

  programeazaScriereaIncadrarii() {
    clearTimeout(this.ceasIncadrare);
    this.ceasIncadrare = setTimeout(() => this.scrieIncadrarea('client'), 250);
  }

  /** Miniatura = exact bucata aleasa, ca omul sa vada ce a ales si dupa ce inchide chenarul. */
  miniaturaDecupata() {
    const local = this.pozaLocala;
    const crop = this.stare.pozaCrop;
    if (!local?.element || !crop) return;
    const panza = document.createElement('canvas');
    const latime = 240;
    panza.width = latime;
    panza.height = Math.max(1, Math.round(latime * crop.h / crop.w));
    const ctx = panza.getContext('2d');
    // Aceeasi geometrie ca pe server: centrul bucatii in centrul miniaturii,
    // poza rotita in jurul lui si desenata intreaga, la scara.
    const k = latime / crop.w;
    ctx.translate(panza.width / 2, panza.height / 2);
    ctx.rotate(((crop.unghi || 0) * Math.PI) / 180);
    ctx.drawImage(local.element, -(crop.x + crop.w / 2) * k, -(crop.y + crop.h / 2) * k, local.latime * k, local.inaltime * k);
    panza.toBlob((blob) => {
      if (!blob || this.pozaLocala !== local) return;
      this.punMiniatura(URL.createObjectURL(blob));
      const nod = this.nodIncadrare ?? [...this.campuri.values()].find(({ camp }) => this.estePoza(camp))?.nod;
      // Cat timp chenarul e deschis, miniatura sta ascunsa: acolo se uita omul
      // la scena, nu la un patrat de 4 rem de langa ea.
      if (nod && nod.querySelector('[data-rol="incadrare"]')?.hidden !== false) {
        this.pozaArata(nod, this.miniaturaUrl);
      }
    }, 'image/jpeg', 0.9);
  }

  /**
   * Gesturile pe scena: un deget muta, doua maresc si rotesc, rotita mareste,
   * sagetile muta, glisoarele maresc si rotesc. Fiecare gest incheiat scrie
   * alegerea; mutarea in sine doar deseneaza.
   *
   * Rotirea cu doua degete porneste abia dupa 4 grade adunate: la o ciupire
   * obisnuita degetele se rotesc putin oricum, si fara pragul asta fiecare
   * marire ar strica orizontala pe care tocmai a gasit-o detectia.
   */
  leagaGesturile(nod) {
    const scena = nod.querySelector('[data-rol="scenapoza"]');
    const zoom = nod.querySelector('[data-rol="zoom"]');
    const rotire = nod.querySelector('[data-rol="rotire"]');
    if (!scena) return;
    const activi = new Map();
    let distanta = null;
    let mijloc = null;
    let unghiDegete = null;
    let rotireAdunata = 0;
    let rotireActiva = false;
    const punct = (ev) => {
      const r = scena.getBoundingClientRect();
      return { x: ev.clientX - r.left, y: ev.clientY - r.top };
    };
    const laDoua = () => {
      const [a, b] = [...activi.values()];
      distanta = Math.hypot(a.x - b.x, a.y - b.y);
      mijloc = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
      unghiDegete = (Math.atan2(b.y - a.y, b.x - a.x) * 180) / Math.PI;
      rotireAdunata = 0;
      rotireActiva = false;
    };

    scena.addEventListener('pointerdown', (ev) => {
      if (!this.inc) return;
      scena.setPointerCapture(ev.pointerId);
      activi.set(ev.pointerId, punct(ev));
      if (activi.size === 2) laDoua();
      ev.preventDefault();
    }, { signal: this.semnal });

    scena.addEventListener('pointermove', (ev) => {
      if (!activi.has(ev.pointerId) || !this.inc) return;
      const acum = punct(ev);
      if (activi.size === 1) {
        const inainte = activi.get(ev.pointerId);
        this.mutaIncadrarea(acum.x - inainte.x, acum.y - inainte.y);
        activi.set(ev.pointerId, acum);
        return;
      }
      activi.set(ev.pointerId, acum);
      if (activi.size === 2) {
        const [a, b] = [...activi.values()];
        const d = Math.hypot(a.x - b.x, a.y - b.y);
        const m = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
        const u = (Math.atan2(b.y - a.y, b.x - a.x) * 180) / Math.PI;
        if (distanta) this.maresteIncadrarea(this.inc.s * (d / distanta));
        if (mijloc) this.mutaIncadrarea(m.x - mijloc.x, m.y - mijloc.y);
        if (unghiDegete !== null) {
          let delta = u - unghiDegete;
          if (delta > 180) delta -= 360;
          if (delta < -180) delta += 360;
          rotireAdunata += delta;
          if (!rotireActiva && Math.abs(rotireAdunata) >= 4) rotireActiva = true;
          if (rotireActiva) this.rotesteIncadrarea(this.inc.unghi + delta);
        }
        distanta = d;
        mijloc = m;
        unghiDegete = u;
      }
    }, { signal: this.semnal });

    const ridica = (ev) => {
      if (!activi.delete(ev.pointerId)) return;
      distanta = null;
      mijloc = null;
      unghiDegete = null;
      if (activi.size === 1) laDoua();
      if (activi.size === 0) this.scrieIncadrarea('client');
    };
    scena.addEventListener('pointerup', ridica, { signal: this.semnal });
    scena.addEventListener('pointercancel', ridica, { signal: this.semnal });

    scena.addEventListener('wheel', (ev) => {
      if (!this.inc) return;
      ev.preventDefault();
      this.maresteIncadrarea(this.inc.s * (ev.deltaY < 0 ? 1.1 : 1 / 1.1));
      this.programeazaScriereaIncadrarii();
    }, { passive: false, signal: this.semnal });

    rotire?.addEventListener('input', () => this.rotesteIncadrarea(Number(rotire.value)), { signal: this.semnal });
    rotire?.addEventListener('change', () => this.scrieIncadrarea('client'), { signal: this.semnal });

    scena.addEventListener('keydown', (ev) => {
      const pas = 12;
      const directie = { ArrowLeft: [pas, 0], ArrowRight: [-pas, 0], ArrowUp: [0, pas], ArrowDown: [0, -pas] }[ev.key];
      if (!directie || !this.inc) return;
      ev.preventDefault();
      this.mutaIncadrarea(directie[0], directie[1]);
      this.programeazaScriereaIncadrarii();
    }, { signal: this.semnal });

    zoom?.addEventListener('input', () => {
      const inc = this.inc;
      if (!inc) return;
      const v = Number(zoom.value) / 100;
      const sMin = this.scaraMinima(inc.unghi);
      this.maresteIncadrarea(sMin * (inc.sMax / sMin) ** v);
    }, { signal: this.semnal });
    zoom?.addEventListener('change', () => this.scrieIncadrarea('client'), { signal: this.semnal });

    // Latimea campului se schimba (rotirea telefonului): chenarul se remasoara
    // si ramane pe aceeasi bucata din poza.
    window.addEventListener('resize', () => {
      if (this.nodIncadrare === nod && !nod.querySelector('[data-rol="incadrare"]').hidden) this.asazaScena(nod);
    }, { signal: this.semnal });
  }

  /**
   * Ochii, cautati dupa incarcare. Cand ii gaseste, chenarul sare pe ei — DOAR
   * daca omul nu l-a mutat intre timp: alegerea lui bate detectia. Cand nu-i
   * gaseste, spune si lasa chenarul unde e.
   */
  async cautaOchii(nod) {
    const local = this.pozaLocala;
    if (!local?.element) return;
    this.sfat(nod, this.vorba('incadrareCauta'));
    let ochi = null;
    try {
      ochi = await gasesteOchii(local.element);
    } catch {
      ochi = null;
    }
    if (this.semnal.aborted || this.pozaLocala !== local) return;
    if (!ochi) { this.sfat(nod, this.vorba('incadrareNegasita')); return; }
    if (this.inc?.atins) { this.sfat(nod, ''); return; }
    const cadru = cadrulDinOchi(ochi, this.aspectulPozei(), local.latime, local.inaltime);
    if (!this.inc) this.asazaScena(nod);
    // Scena inca nu s-a putut masura (pasul e ascuns): ochii se pierd, chenarul
    // ramane centrat si omul il muta cand ajunge la el. Mai bine decat o eroare.
    if (!this.inc) { this.sfat(nod, ''); return; }
    this.asazaCadrulPe(cadru);
    this.inc.sursa = 'auto';
    this.scrieIncadrarea('auto');
    const indreptata = Math.abs(cadru.unghi) >= 1;
    this.sfat(nod, this.vorba(ochi.fete > 1 ? 'incadrareMulte' : (indreptata ? 'incadrareIndreptata' : 'incadrareGasita')));
  }

  /**
   * Starea campului de poza.
   *
   * Miniatura vine din fisierul local, deci exista doar cat traieste elementul; poza incarcata
   * traieste in `STARE` si peste o re-randare. De aia butoanele se uita la stare, nu la imagine:
   * altfel, dupa o schimbare de varianta, un client cu poza deja incarcata ar fi indemnat s-o
   * incarce din nou.
   */
  /*
   * Miniatura NU mai revoca adresa pe care tocmai o primeste.
   *
   * Aici statea: „Gata" chema `pozaArata(nod, this.miniaturaUrl)`, iar
   * `miniatura.src` era DEJA `this.miniaturaUrl` — deci se revoca fix adresa
   * care urma sa fie pusa, si in locul ochilor ramanea o iconita de imagine
   * rupta. Adresele blob au acum un singur proprietar: `miniaturaUrl` si
   * `pozaLocala.url` se elibereaza acolo unde se inlocuiesc, nicaieri altundeva.
   */
  pozaArata(nod, adresaLocala) {
    const miniatura = nod.querySelector('[data-rol="miniatura"]');
    if (adresaLocala) miniatura.src = adresaLocala; else miniatura.removeAttribute('src');
    miniatura.hidden = !adresaLocala;

    const are = Boolean(adresaLocala) || Boolean(this.stare.poza);
    nod.querySelector('[data-rol="alege"]').textContent = this.vorba(are ? 'schimba' : 'alege');
    const scoate = nod.querySelector('[data-rol="scoate"]');
    scoate.textContent = this.vorba('scoate');
    scoate.hidden = !are;
    // Reincadrarea cere poza in browser: dupa un refresh starea stie chenarul,
    // dar pixelii nu mai sunt — butonul dispare, alegerea ramane.
    const reincadreaza = nod.querySelector('[data-rol="reincadreaza"]');
    if (reincadreaza) {
      reincadreaza.querySelector('[data-rol="reincadrareeticheta"]').textContent = this.vorba('reincadreaza');
      const seVede = Boolean(are && this.pozaLocala && this.stare.pozaCrop);
      reincadreaza.hidden = !seVede;
      // Accentul se aprinde cand butonul CHIAR se vede — adica in clipa in care
      // omul a inchis chenarul si a ramas cu miniatura mica in fata. Pornit la
      // incarcare, pulsul curgea in spatele chenarului deschis si se termina
      // pana sa apara butonul: un accent pe care nu-l vede nimeni.
      reincadreaza.classList.toggle('gravura__reincadreaza--nou', seVede && this.reincadrareNoua === true);
    }
  }

  /** Adresele blob ale miniaturii: una singura la un moment dat, eliberata aici. */
  punMiniatura(adresa) {
    if (this.miniaturaUrl && this.miniaturaUrl !== adresa) URL.revokeObjectURL(this.miniaturaUrl);
    this.miniaturaUrl = adresa ?? null;
  }

  /** La fel, pentru poza intreaga tinuta pentru chenar. */
  punPozaLocala(local) {
    if (this.pozaLocala?.url && this.pozaLocala.url !== local?.url) URL.revokeObjectURL(this.pozaLocala.url);
    this.pozaLocala = local ?? null;
  }

  async incarcaPoza(nod, fisier) {
    if (!fisier) return;
    if (!/^image\/(jpeg|png|webp)$/.test(fisier.type)) { this.noteaza(nod, this.vorba('pozaTip'), true); return; }
    if (fisier.size > POZA_MAX_OCTETI) {
      this.noteaza(nod, this.vorba('pozaMare', { mb: Math.round(POZA_MAX_OCTETI / 1024 / 1024) }), true);
      return;
    }

    this.noteaza(nod, this.vorba('pozaSeIncarca'));
    this.inchideIncadrarea(nod);
    this.inc = null;
    this.punPozaLocala(null);
    this.punMiniatura(null);
    this.stare.pozaCrop = null;
    this.deseneazaFataCuPoza();

    // Rotirea EXIF coapta si latura lunga la 2000 px — vezi `pregatestePoza`.
    // Daca nu se poate (o poza uriasa pe un telefon vechi), pleaca originalul
    // si nu se ofera chenarul: pixelii din browser n-ar mai fi cei din fisier.
    let pregatita = null;
    try {
      pregatita = await this.pregatestePoza(fisier);
    } catch {
      pregatita = null;
    }
    const deTrimis = pregatita
      ? new File([pregatita.blob], `poza.${pregatita.tip === 'image/png' ? 'png' : 'jpg'}`, { type: pregatita.tip })
      : fisier;

    const pachet = new FormData();
    pachet.append('poza', deTrimis);
    try {
      const raspuns = await this.cere('upload', { method: 'POST', body: pachet });
      // Din `{cale, adresa}` se pastreaza CALEA. Adresa semnata expira, si oricum o pune serverul
      // pe comanda; calea e cea care merge in jeton si apoi in `crm_designs.poza_path`.
      this.stare.poza = raspuns.cale ?? null;
      if (!this.stare.poza) throw new Error('raspuns fara cale');
      this.noteaza(nod, '');
      // Miniatura ramane locala: o poza de ochi e data biometrica, iar adresa semnata n-are de
      // ce sa stea intr-un atribut din pagina.
      if (pregatita) {
        const url = URL.createObjectURL(pregatita.blob);
        const element = await imagineDin(url);
        this.punPozaLocala({ url, element, latime: pregatita.latime, inaltime: pregatita.inaltime });
        this.punMiniatura(null);
        // Poza asta e noua: butonul de reincadrare se va aprinde cand apare.
        this.reincadrareNoua = true;
        this.pozaArata(nod, null);
        this.deschideIncadrarea(nod);
        this.cautaOchii(nod);
      } else {
        this.punPozaLocala(null);
        this.punMiniatura(URL.createObjectURL(fisier));
        this.pozaArata(nod, this.miniaturaUrl);
      }
    } catch {
      if (this.semnal.aborted) return;
      this.stare.poza = null;
      this.noteaza(nod, this.vorba('pozaEroare'), true);
    }
    this.schimbat();
  }

  /**
   * Fontul, aratat cu fontul.
   *
   * Lista derulanta cerea clientului sa-si imagineze cum arata „Script fin".
   * Cardurile scriu numele CU fontul — trasee din acelasi motor care graveaza,
   * deci ce se vede e ce iese pe metal. Fara webfonturi in pagina de produs.
   */
  alegereaFontului() {
    const nod = this.cloneaza('carduri');
    nod.setAttribute('role', 'radiogroup');
    nod.setAttribute('aria-label', this.vorba('font'));
    this.cardFont = new Map();

    for (const font of this.config.fonturi ?? []) {
      const card = this.cloneaza('card-font');
      const intrare = card.querySelector('[data-rol="intrare"]');
      intrare.name = this.identificator('font');
      intrare.value = String(font.id);
      intrare.checked = String(font.id) === String(this.stare.font);
      intrare.id = this.identificator(`font-${font.id}`);
      card.querySelector('[data-rol="nume"]').textContent = font.eticheta;

      const semn = card.querySelector('[data-rol="semn"]');
      // Fontul fara fisier se ofera, dar spune ca previzualizarea e aproximativa.
      if (font.aproximativ) semn.textContent = this.vorba('fontAproximativ');
      else semn.remove();

      intrare.addEventListener('change', () => {
        if (!intrare.checked) return;
        this.stare.font = Number(intrare.value);
        this.schimbat();
        for (const cod of this.campuri.keys()) this.programeazaAsezare(cod, 0);
      }, { signal: this.semnal });

      this.cardFont.set(String(font.id), card);
      nod.append(card);
    }

    this.notaFont = document.createElement('p');
    this.notaFont.className = 'gravura__nota text-xs';
    this.notaFont.setAttribute('aria-live', 'polite');

    const invelis = document.createElement('div');
    invelis.className = 'gravura__grup';
    invelis.append(nod, this.notaFont);
    return invelis;
  }

  /**
   * Fonturile care nu pot scrie ce s-a tastat se sting, cu motivul scris in eticheta.
   *
   * S-a intamplat deja: „Cinzel nu are chirilica — un client rusofon primea patrate goale".
   * Diferenta e ca acum se vede inainte de plata, nu dupa gravare.
   */
  actualizeazaFonturi() {
    if (!this.cardFont) return;
    const tot = Object.values(this.stare.texte).join(' ');
    this.motivFont = null;

    for (const [id, card] of this.cardFont) {
      const font = (this.config.fonturi ?? []).find((f) => String(f.id) === id);
      const lipsa = font ? litereLipsa(font, tot) : [];
      const motiv = lipsa.length === 0
        ? null
        : this.vorba('fontFaraLitere', { font: font.eticheta, litere: lipsa.join(' ') });

      const intrare = card.querySelector('[data-rol="intrare"]');
      intrare.disabled = Boolean(motiv);
      card.classList.toggle('gravura__card--stins', Boolean(motiv));
      // Motivul sta pe card, nu doar in randul de sub: cine cauta „de ce nu pot
      // alege ala" se uita la card.
      card.title = motiv ?? (font?.eticheta ?? '');
      if (motiv && intrare.checked) this.motivFont = motiv;
    }

    this.notaFont.textContent = this.motivFont ? this.vorba('fontAles', { motiv: this.motivFont }) : '';
    this.notaFont.classList.toggle('gravura__nota--rau', Boolean(this.motivFont));

    // Fonturile stinse cad la coada listei: alegerea corecta e prima, nu
    // ratacita printre carduri pale. Ordinea dintre cele bune nu se schimba.
    const parinte = [...this.cardFont.values()][0]?.parentElement;
    if (parinte) {
      const bune = []; const stinse = [];
      for (const card of this.cardFont.values()) {
        (card.classList.contains('gravura__card--stins') ? stinse : bune).push(card);
      }
      const ordinea = [...bune, ...stinse];
      if (ordinea.some((card, index) => parinte.children[index] !== card)) {
        parinte.append(...ordinea);
      }
    }
  }

  /**
   * Cutia, cu poza ei.
   *
   * Ascunse intr-o lista derulanta, cutiile nu se vindeau: „Premium Neagra" nu
   * spune nimic pana nu se vede. Cardurile arata poza si pretul in moneda
   * pietei, iar „fara cutie" e o optiune vizibila, nu valoarea implicita ascunsa.
   */
  alegereaImpachetarii() {
    const nod = this.cloneaza('carduri');
    nod.classList.add('gravura__carduri--cutii');
    nod.setAttribute('role', 'radiogroup');
    nod.setAttribute('aria-label', this.vorba('impachetare'));
    const nume = this.identificator('impachetare');

    const pune = (cutie) => {
      const card = this.cloneaza('card-cutie');
      const intrare = card.querySelector('[data-rol="intrare"]');
      // Identitatea e ETICHETA, nu SKU-ul: sub ea o valideaza serverul, sub ea intra in
      // proprietatea „Împachetare", si sunt cutii fara SKU — acolo cheia ar fi fost `null`.
      intrare.name = nume;
      intrare.value = cutie ? cutie.eticheta : '';
      intrare.checked = (cutie?.eticheta ?? '') === (this.stare.impachetare ?? '');
      intrare.id = this.identificator(`cutie-${cutie ? cutie.eticheta.replace(/\W+/g, '-') : 'fara'}`);

      const poza = card.querySelector('[data-rol="poza"]');
      const lupa = card.querySelector('[data-rol="lupa"]');
      if (cutie?.poza) {
        poza.src = cutie.poza;
        poza.alt = cutie.pozaAlt ?? cutie.eticheta;

        // Cutia se vinde din poza: cine nu vede ce contine „Premium Negru +"
        // alege cea mai ieftina sau niciuna. Miniatura de 120px nu arata nici
        // panglica, nici cartonasul.
        lupa.hidden = false;
        lupa.setAttribute('aria-label', `${this.vorba('vezi')}: ${cutie.eticheta}`);
        lupa.addEventListener('click', (ev) => {
          // Butonul sta INTR-UN <label>: fara oprirea propagarii, un click pe
          // lupa ar bifa si cutia. Cine voia doar sa se uite ar fi cumparat-o.
          ev.preventDefault();
          ev.stopPropagation();
          this.deschideVitrina(cutie);
        }, { signal: this.semnal });
      } else {
        poza.remove();
        lupa?.remove();
      }

      card.querySelector('[data-rol="nume"]').textContent = cutie ? cutie.eticheta : this.vorba('impachetareSimpla');
      const pret = card.querySelector('[data-rol="pret"]');
      // Ultima placa NU e „fara impachetare": orice comanda pleaca ambalata.
      // Ce se alege aici e o CUTIE CADOU peste ambalajul simplu, care e inclus.
      // Scris „fara impachetare", clientul intelegea ca primeste piesa goala si
      // se simtea impins spre o cutie platita; scris „gratis", vede ce primeste.
      const scris = cutie ? this.pretulCutiei(cutie) : this.vorba('gratis');
      if (scris) pret.textContent = scris; else pret.remove();

      intrare.addEventListener('change', () => {
        if (!intrare.checked) return;
        this.stare.impachetare = intrare.value;
        // Alta cutie, alta lista de mesaje: alegerea veche nu se poarta mai departe.
        this.stare.impachetareDetaliu = '';
        this.arataDetaliul(detaliu);
        this.schimbat();
      }, { signal: this.semnal });
      // A doua apasare pe cutia deja aleasa o scoate. Fara cardul „Impachetare
      // simpla", altfel nu s-ar mai putea reveni la „fara cutie" dupa ce ai bifat
      // una. `click` vine inaintea lui `change`, deci starea e inca cea veche.
      intrare.addEventListener('click', () => {
        if (!cutie || this.stare.impachetare !== intrare.value) return;
        intrare.checked = false;
        this.stare.impachetare = '';
        this.stare.impachetareDetaliu = '';
        this.arataDetaliul(detaliu);
        this.schimbat();
      }, { signal: this.semnal });
      nod.append(card);
    };

    for (const cutie of this.config.impachetari ?? []) pune(cutie);
    // „Impachetare simpla · Gratis" dubla cutia gratuita din lista („Simpla ·
    // Gratis") — doua carduri cu acelasi pret si acelasi inteles (scos pe 26 sep
    // 2026, cerut de Vlad). Ramane doar cand NU exista o cutie gratuita: atunci
    // e singurul fel de a spune „nu vreau cutie platita".
    const areGratuita = (this.config.impachetari ?? []).some((cutie) => Number(cutie.pret) === 0);
    if (!areGratuita) pune(null);

    // Al doilea rand de carduri: alegerea de PE cutie (mesajul de pe cutia cu
    // mesaj). Se umple in functie de cutia aleasa; gol cand cutia n-are asa ceva.
    //
    // Sta LANGA grila, nu in ea: pe telefon cutiile sunt un rand care se trage
    // cu degetul, iar alegerea mesajului ar fi ajuns ultimul card din rand —
    // in afara ecranului, exact cand omul trebuia s-o vada.
    const invelis = document.createElement('div');
    invelis.className = 'gravura__detaliu';
    invelis.hidden = true;
    const detaliu = invelis;
    this.detaliuNod = invelis;
    this.arataDetaliul(invelis);
    const cutii = document.createElement('div');
    cutii.className = 'gravura__cutii';
    cutii.append(nod, invelis);
    return cutii;
  }

  /** Cutia aleasa, cu tot cu configuratia ei. */
  cutiaAleasa() {
    return (this.config?.impachetari ?? []).find((c) => c.eticheta === this.stare.impachetare) ?? null;
  }

  /**
   * Cardurile alegerii de pe cutie. Un radio per optiune, cu poza si — daca
   * intra in comanda ca linie — pretul. Identitatea e ETICHETA, ca la cutii.
   */
  arataDetaliul(invelis) {
    if (!invelis) return;
    invelis.replaceChildren();
    const cutie = this.cutiaAleasa();
    const detaliu = cutie?.detaliu;
    if (!detaliu || !(detaliu.optiuni ?? []).length) { invelis.hidden = true; return; }

    const limba = this.dataset.limba === 'en' ? 'en' : (document.documentElement.lang || '').startsWith('ru') ? 'ru' : 'ro';
    const titlu = document.createElement('p');
    titlu.className = 'gravura__detaliu-titlu smallcaps';
    titlu.textContent = detaliu.eticheta?.[limba] ?? detaliu.eticheta?.ro ?? '';
    invelis.append(titlu);

    const carduri = this.cloneaza('carduri');
    carduri.classList.add('gravura__carduri--cutii', 'gravura__carduri--detaliu');
    carduri.setAttribute('role', 'radiogroup');
    carduri.setAttribute('aria-label', titlu.textContent);
    const nume = this.identificator('impachetare-detaliu');

    for (const optiune of detaliu.optiuni) {
      const card = this.cloneaza('card-cutie');
      const intrare = card.querySelector('[data-rol="intrare"]');
      intrare.name = nume;
      intrare.value = optiune.eticheta;
      intrare.checked = optiune.eticheta === this.stare.impachetareDetaliu;
      intrare.id = this.identificator(`detaliu-${optiune.eticheta.replace(/\W+/g, '-')}`);
      const poza = card.querySelector('[data-rol="poza"]');
      card.querySelector('[data-rol="lupa"]')?.remove();
      if (optiune.poza) { poza.src = optiune.poza; poza.alt = optiune.eticheta; } else poza.remove();
      card.querySelector('[data-rol="nume"]').textContent = optiune.eticheta;
      const pret = card.querySelector('[data-rol="pret"]');
      const scris = Number.isFinite(optiune.pret) && optiune.pret > 0 ? this.pretulCutiei(optiune) : '';
      if (scris) pret.textContent = scris; else pret.remove();
      intrare.addEventListener('change', () => {
        if (!intrare.checked) return;
        this.stare.impachetareDetaliu = intrare.value;
        this.schimbat();
      }, { signal: this.semnal });
      carduri.append(card);
    }
    invelis.append(carduri);
    invelis.hidden = false;
  }

  programeazaAsezare(cod, intarziere = AMANARE_MS) {
    const intrare = this.campuri.get(cod);
    // Campul de poza n-are previzualizare de text — si nici `scena` in care sa deseneze.
    if (!intrare || this.estePoza(intrare.camp)) return;
    clearTimeout(this.ceasuri.get(cod));

    if (!intrare.control.value.trim()) {
      // Textul sters sterge si desenul. O previzualizare ramasa in urma arata o gravura pe care
      // nimeni n-a comandat-o, iar cifrele de sub ea ar fi ale altui text.
      this.golesteScena(cod);
      intrare.nod.querySelector('[data-rol="cota"]').textContent = '';
      const lizibil = intrare.nod.querySelector('[data-rol="lizibil"]');
      if (lizibil) lizibil.hidden = true;
      this.scrieRandurile(cod, null);
      this.noteaza(intrare.nod, '');
      delete this.stare.verdicte[cod];
      this.programeazaMostrele();
      return;
    }
    this.ceasuri.set(cod, setTimeout(() => this.asezare(cod), intarziere));
  }

  async asezare(cod) {
    const { camp, nod, control } = this.campuri.get(cod);
    const text = control.value.trim();
    if (!text) return;

    let raspuns;
    try {
      raspuns = await this.cere('asezare', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          handle: this.dataset.handle, parte: camp.parte, text, font: this.stare.font,
          asezare: this.asezareaCampului(),
        }),
      });
    } catch {
      return; // O cadere de retea nu strica ce se vede deja pe ecran.
    }
    // Un raspuns intarziat, pentru un text care intre timp s-a schimbat, nu
    // desenaza peste cel nou: pe o retea lenta, literele ar sari inapoi.
    if (control.value.trim() !== text) return;

    const randuri = text.split('\n').filter((rand) => rand.trim()).length || 1;
    this.ultimeleAsezari.set(cod, { raspuns, randuri });
    this.deseneazaScena(cod, compune(raspuns, randuri, { macheta: this.aratamMacheta(), zoom: this.zoom }));
    // Inaltimea plansei, aleasa in consola per produs: pe o cruce inalta,
    // cei 84 px impliciti o faceau de nerecunoscut.
    const scena = this.scene.get(cod);
    const plansaPx = Number(raspuns?.zona?.plansaPx);
    if (scena && plansaPx > 0) scena.style.setProperty('--gravura-plansa', `${plansaPx}px`);
    nod.querySelector('[data-rol="cota"]').textContent = this.cota(raspuns);
    this.scrieLizibilitatea(cod, raspuns);
    this.scrieRandurile(cod, raspuns);
    this.scrieUmplerea(cod, raspuns);
    this.scrieMasura(raspuns);
    this.programeazaMostrele();
    const eraRau = this.stare.verdicte[cod] === false;
    this.stare.verdicte[cod] = raspuns.ok !== false;
    const rele = raspuns.ok === false ? (raspuns.probleme ?? []).map(mesajul).filter(Boolean) : [];
    this.noteaza(nod, rele.join('. '), true);
    this.aplicaBlocajul();
    this.actualizeazaRezumatul();

    // Verdictul vine DUPA ce s-a programat validarea tacuta, iar aceea se opreste
    // singura cat timp formularul e blocat. Cine scria un text prea lung si apoi
    // il scurta ramanea deci fara jeton si fara proprietati: gravura se vedea pe
    // ecran, dar comanda ar fi plecat goala. Cand verdictul se indreapta, se
    // programeaza din nou.
    if (eraRau && this.stare.verdicte[cod] && this.motivulBlocarii() === null) {
      this.programeazaValidarea();
    }
  }

  /**
   * Contorul de langa eticheta. Scurt dinadins: sta pe acelasi rand cu numele campului si cu
   * nasturii de simboluri, iar acolo o propozitie intreaga ar imbranci totul pe randul urmator.
   * Ce e de masura — cat de mare iese litera, cat e zona — sta sub planșă, unde se si vede.
   */
  cota(raspuns) {
    if (!Number.isFinite(raspuns.caractere_ramase)) return '';
    // Cu masura de lizibilitate pe acelasi rand, cifra de caractere nu mai are
    // ce spune: e o estimare pe litere medii, iar pastila masoara chiar textul
    // scris. „20 in plus" langa „Se citeste bine" ar fi doua raspunsuri la
    // aceeasi intrebare, si unul ar minti.
    if (this.querySelector('[data-rol="lizibil"]')) return '';
    // Cifra poate iesi negativa, si asa trebuie citita: „3 in plus" spune ce e de sters, pe cand
    // un zero ar spune doar ca s-a atins limita.
    return raspuns.caractere_ramase < 0
      ? this.vorba('prisosScurt', { n: -raspuns.caractere_ramase })
      : this.vorba('ramaseScurt', { n: raspuns.caractere_ramase });
  }

  /**
   * Cat din latimea utila e ocupata — o bara, nu o cifra: „aproape plin" se
   * vede dintr-o privire, inainte sa apara vreun mesaj de eroare.
   */
  scrieUmplerea(cod, raspuns) {
    const gazda = this.umpleri?.get(cod);
    if (!gazda) return;
    const umplere = Number(raspuns.umplere);
    if (!Number.isFinite(umplere)) { gazda.hidden = true; return; }
    gazda.hidden = false;
    const bara = gazda.querySelector('[data-rol="umplere-bara"]');
    if (bara) bara.style.width = `${Math.round(Math.min(1, umplere) * 100)}%`;
    gazda.classList.toggle('gravura__umplere--plin', umplere > 0.96 || raspuns.ok === false);
  }

  /** Legenda de sub planșă: cat e zona pe piesa reala, cat iese litera, si daca e masurata. */
  scrieMasura(raspuns) {
    if (!this.cotaPlansa) return;
    const zona = raspuns.zona ?? {};
    const bucati = [];
    if (Number.isFinite(zona.latimeMm) && Number.isFinite(zona.inaltimeMm)) {
      bucati.push(this.vorba('zonaMm', {
        w: Number(zona.latimeMm).toFixed(0),
        h: Number(zona.inaltimeMm).toFixed(0),
      }));
    }
    // Pe machetă, fara milimetrii literei: acolo clientul se uita la bijuterie,
    // iar „litera ~2 mm" il facea sa ezite pe un text care se graveaza bine.
    // Desenul tehnic (`plansa`) ii pastreaza — acolo sunt cote.
    if (Number.isFinite(raspuns.inaltime_litera_mm) && !this.aratamMacheta()) {
      bucati.push(this.vorba('litera', { mm: raspuns.inaltime_litera_mm.toFixed(1) }));
    }
    // Marcajul „estimat" nu se ascunde: spune ca zona n-a fost masurata pe piesa reala.
    if (zona.estimat) bucati.push(this.vorba('estimat'));
    this.cotaPlansa.textContent = bucati.join(' · ');
  }


  /**
   * Partajarea a ce s-a compus.
   *
   * „Vezi cum arata?" — se trimite partenerului un link care redeschide produsul
   * cu textele, fontul si cutia deja puse. Starea intra in adresa, nu pe server:
   * nu se scrie nimic si nu expira nimic. Poza nu intra — o poza de ochi e data
   * biometrica si n-are ce cauta intr-un link trimis mai departe.
   */
  linkulCompunerii() {
    const stare = {
      t: this.stare.texte,
      f: this.stare.font,
      i: this.stare.impachetare || undefined,
      d: this.stare.impachetareDetaliu || undefined,
    };
    const adresa = new URL(window.location.href);
    adresa.searchParams.set('gravura', btoa(unescape(encodeURIComponent(JSON.stringify(stare)))));
    return adresa.toString();
  }

  /** Starea din adresa, daca cineva a deschis un link partajat. */
  citesteLinkul() {
    const brut = new URLSearchParams(window.location.search).get('gravura');
    if (!brut) return;
    try {
      const stare = JSON.parse(decodeURIComponent(escape(atob(brut))));
      if (stare.t && typeof stare.t === 'object') {
        for (const [cod, text] of Object.entries(stare.t)) {
          if (typeof text === 'string') this.stare.texte[cod] = text.slice(0, MAX_CARACTERE);
        }
      }
      if (Number.isFinite(Number(stare.f))) this.stare.font = Number(stare.f);
      if (typeof stare.i === 'string') this.stare.impachetare = stare.i;
      if (typeof stare.d === 'string') this.stare.impachetareDetaliu = stare.d;
    } catch { /* link stricat: se deschide formularul gol, nu o eroare */ }
  }

  /** Nasturele de partajare, in antetul previzualizarii. */
  nasturePartajare() {
    const nasture = document.createElement('button');
    nasture.type = 'button';
    nasture.className = 'gravura__partaj text-xs';
    nasture.textContent = this.vorba('partajeaza');
    nasture.addEventListener('click', async () => {
      const link = this.linkulCompunerii();
      const titlu = document.title;
      try {
        // Pe telefon, foaia de partajare a sistemului; pe desktop, clipboard.
        if (navigator.share) await navigator.share({ title: titlu, url: link });
        else {
          await navigator.clipboard.writeText(link);
          nasture.textContent = this.vorba('partajatCopiat');
          setTimeout(() => { nasture.textContent = this.vorba('partajeaza'); }, 2500);
        }
      } catch { /* anulat de client: nimic de raportat */ }
    }, { signal: this.semnal });
    return nasture;
  }

  /** Orice atingere invalideaza jetonul: ce se adauga in cos trebuie sa fie ce se vede. */
  schimbat() {
    this.stare.jeton = null;
    this.stare.amprenta = null;
    this.scrieProprietati({});
    this.actualizeazaFonturi();
    this.aplicaBlocajul();
    this.actualizeazaTotalul();
    this.actualizeazaRezumatul();
    this.salveazaStarea();
    this.programeazaValidarea();
    // Jetonul si macheta tocmai au disparut din formular. Fereastra de comanda,
    // daca e deschisa, trebuie sa arate ca se lucreaza la macheta — nu macheta
    // veche, a textului de dinainte.
    this.dispatchEvent(new CustomEvent('gravura:schimbat', { bubbles: true }));
  }

  /** Ce a scris clientul, pe disc — refresh-ul nu mai sterge nimic. */
  salveazaStarea() {
    try {
      const { texte, font, poza, pozaCrop, impachetare, impachetareDetaliu, macheta, maiTarziu } = this.stare;
      localStorage.setItem(cheiaPastrarii(this.dataset.handle), JSON.stringify({
        v: 1, cand: Date.now(), texte, font, poza, pozaCrop, impachetare, impachetareDetaliu, macheta, maiTarziu,
      }));
    } catch { /* mod privat sau disc plin: starea ramane doar in memorie */ }
  }

  /**
   * Scrierea proactiva a proprietatilor — pentru butoanele care NU trec prin
   * submit: COD-ul Releasit si Buy Now serializeaza formularul la click, deci
   * proprietatile trebuie sa fie DEJA acolo. Se valideaza in tacere cand
   * formularul e complet; la esec nu se arata nimic — mesajele raman ale
   * drumului normal, la apasarea butonului nostru.
   */
  programeazaValidarea() {
    clearTimeout(this.ceasValidare);
    this.ceasValidare = setTimeout(() => this.valideazaLinistit(), AMANARE_VALIDARE_MS);
  }

  async valideazaLinistit() {
    if (this.stare.nefunctional || this.trimit || this.valideazaInMers) return;
    if (this.stare.jeton && this.stare.amprenta === this.amprenta()) return;

    if (this.stare.maiTarziu) { this.amanaTextul(); return; }
    if (this.motivulBlocarii() !== null) return;

    this.valideazaInMers = true;
    try {
      await this.valideaza({ tacut: true });
    } finally {
      this.valideazaInMers = false;
    }
  }

  /**
   * Ce a scris clientul PANA ACUM, chiar daca inca nu s-a validat nimic.
   *
   * Popup-ul de comanda arata un rezumat („ce comanzi"). Pana ieri il citea
   * doar din proprietatile scrise de validare — iar validarea nu ruleaza cat
   * timp lipseste ceva obligatoriu (o poza, de exemplu). Rezultatul: omul
   * scrisese textul, il vedea in formular, si rezumatul spunea „inca fara
   * text de gravat". Aici sunt valorile din campuri, exact cum se vad.
   */
  rezumatulCampurilor() {
    const randuri = [];

    for (const { camp, control } of this.campuri.values()) {
      if (this.estePoza(camp)) {
        if (this.stare.poza) randuri.push({ eticheta: this.eticheta(camp), poza: true });
        continue;
      }
      const text = control.value.trim();
      if (text) randuri.push({ eticheta: this.eticheta(camp), valoare: text });
    }

    const font = (this.config?.fonturi ?? []).find((f) => f.id === this.stare.font);
    if (font && randuri.length > 0) {
      randuri.push({ eticheta: this.vorba('font'), valoare: font.eticheta });
    }

    return randuri;
  }

  /**
   * Motivul blocarii, cu detaliul scris langa camp.
   *
   * `motivulBlocarii()` da verdictul scurt — „De corectat" — care ajunge langa
   * un buton, la un pas de campul care spune restul. In fereastra de comanda
   * campul NU se vede: acolo trebuie sa ajunga si propozitia intreaga
   * („De corectat: 19 caractere, sablonul permite 14"), altfel omul citeste ca
   * are ceva de reparat si nu afla ce.
   */
  motivulDetaliat() {
    const scurt = this.motivulBlocarii();
    if (!scurt) return null;
    for (const { nod } of this.campuri.values()) {
      const nota = nod.querySelector('[data-rol="nota"]');
      if (nota?.classList.contains('gravura__nota--rau') && nota.textContent.trim()) {
        return nota.textContent.trim();
      }
    }
    return scurt;
  }

  motivulBlocarii() {
    // Cutia cu alegere obligatorie si nimic ales: formularul nu pleaca. Se
    // verifica INAINTEA lui „mai tarziu" — amanarea e a textului, nu a cutiei.
    const cutie = this.cutiaAleasa?.();
    if (cutie?.detaliu?.obligatoriu && !this.stare.impachetareDetaliu) return this.vorba('detaliuLipsa');
    if (this.stare.maiTarziu) return null;
    if (this.motivFont) return this.vorba('fontAles', { motiv: this.motivFont });
    for (const [cod, { camp, control }] of this.campuri) {
      const gol = this.estePoza(camp) ? !this.stare.poza : !control.value.trim();
      if (gol && camp.obligatoriu) return this.vorba('gol');
      if (!gol && this.stare.verdicte[cod] === false) return this.vorba('deCorectat');
    }
    return null;
  }

  /**
   * Semnul de pe butonul de adaugare, cand ceva nu e in regula.
   *
   * Se pune `aria-disabled`, nu `disabled`: butonul e randat de tema si inlocuit la fiecare
   * schimbare de varianta, iar unul din bara lipita e legat prin `form=` si sta in alta parte a
   * paginii. Oprirea reala o face `laTrimitere`; asta e doar partea vizibila.
   */
  aplicaBlocajul() {
    const blocat = Boolean(this.motivulBlocarii());
    const id = this.dataset.formular;
    const nasturi = [
      ...(this.formular?.querySelectorAll('[type="submit"]') ?? []),
      ...(id ? document.querySelectorAll(`[form="${id}"][type="submit"]`) : []),
    ];
    for (const nasture of nasturi) {
      if (blocat) nasture.setAttribute('aria-disabled', 'true');
      else nasture.removeAttribute('aria-disabled');
    }
  }

  varianta() { return this.formular ? new FormData(this.formular).get('id') : null; }

  amprenta() {
    const { texte, font, poza, pozaCrop, impachetare, impachetareDetaliu, macheta, maiTarziu } = this.stare;
    return JSON.stringify([this.varianta(), texte, font, poza, pozaCrop, impachetare, impachetareDetaliu, macheta, maiTarziu]);
  }

  async laTrimitere(ev) {
    if (this.stare.nefunctional || this.trimit) return;
    if (this.stare.jeton && this.stare.amprenta === this.amprenta()) return;

    ev.preventDefault();
    ev.stopImmediatePropagation();
    const trimitator = ev.submitter;
    this.trimit = true;
    try {
      if (this.stare.maiTarziu) this.amanaTextul();
      else if (!await this.valideaza()) return;
      await this.adaugaImpachetarea();
      // A doua trecere prin acelasi ascultator vede `trimit` si lasa evenimentul sa treaca la
      // <product-form>, cu proprietatile deja in formular.
      this.formular.requestSubmit(trimitator ?? undefined);
    } finally {
      this.trimit = false;
    }
  }

  /**
   * „Trimit textul mai tarziu".
   *
   * Se sare peste `/gravura/valideaza`, unde o linie fara nimic completat e eroare — si asa
   * trebuie sa ramana pentru cine chiar voia gravura. Bifa e alegerea explicita a clientului, iar
   * linia pleaca insemnata, ca sa apara in coada `lipsa_date` din consola in loc sa treaca drept
   * bijuterie negravata.
   *
   * Numele proprietatii e scris aici, nu de server: `scrie()` din `src/gravura/proprietati.js` nu
   * cunoaste inca situatia asta, iar tabelul din §2 n-are un nume pentru ea. Prefixul `_` o ascunde
   * de client si o tine departe de aliasurile de text — o proprietate vizibila numita „Gravură"
   * s-ar citi drept JETON, fiindca acolo duce cheia normalizata `gravura`.
   */
  amanaTextul() {
    this.scrieProprietati({ _gravura_mai_tarziu: 'da' });
  }

  async valideaza({ tacut = false } = {}) {
    if (!tacut) this.spuneStarea(this.vorba('seVerifica'));
    let raspuns;
    try {
      raspuns = await this.cere('valideaza', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          handle: this.dataset.handle,
          reteta: this.dataset.reteta,
          varianta: this.varianta(),
          font: this.stare.font,
          fete: [...this.campuri.values()]
            .filter(({ camp }) => !this.estePoza(camp))
            // `asezare`: serverul rupe randurile EXACT ca in previzualizare si
            // ingheata ruperea in comanda — laserul graveaza ce s-a vazut aici.
            .map(({ camp, control }) => ({
              parte: camp.parte, cod: camp.cod, text: control.value.trim(), asezare: this.asezareaCampului(),
            })),
          poza: this.stare.poza,
          // Bucata aleasa din poza (ochii). Doar cu poza; fara ea serverul o refuza.
          poza_crop: this.stare.poza && this.stare.pozaCrop ? this.stare.pozaCrop : undefined,
          impachetare: this.stare.impachetare,
          impachetare_detaliu: this.stare.impachetareDetaliu || undefined,
          macheta: this.stare.macheta,
          mai_tarziu: this.stare.maiTarziu,
        }),
      });
    } catch {
      if (!tacut) this.spuneStarea(this.vorba('eroareRetea'));
      return false;
    }

    if (!raspuns.ok) {
      // In modul tacut, erorile nu se striga: clientul inca scrie. Ele apar pe
      // drumul normal, cand chiar apasa butonul.
      if (!tacut) this.arataErorile(raspuns.erori ?? []);
      return false;
    }
    this.scrieProprietati(raspuns.proprietati ?? {});
    this.stare.jeton = raspuns.token ?? null;
    this.stare.amprenta = this.amprenta();
    if (!tacut) this.spuneStarea('');
    // Macheta si celelalte proprietati tocmai au aparut in formular. Fereastra
    // de comanda, daca e deschisa, isi reface rezumatul din ele — altfel omul
    // isi corecteaza textul si vede in continuare un loc gol acolo unde tocmai
    // s-a facut desenul.
    this.dispatchEvent(new CustomEvent('gravura:validat', { bubbles: true }));
    return true;
  }

  /**
   * Campul dintr-o eroare a serverului.
   *
   * `camp` vine in doua scrieri: codul campului la ce lipseste („text_fata") si NUMELE FETEI la
   * ce a verificat motorul („fata"). Se cauta in ambele, altfel jumatate din motive ar sari peste
   * campul lor si ar cadea in randul de stare, departe de ochi.
   */
  campulDupa(cheie) {
    if (!cheie) return null;
    return this.campuri.get(cheie)
      ?? [...this.campuri.values()].find(({ camp }) => camp.parte === cheie)
      ?? null;
  }

  /** Motivul sta langa campul vinovat; ce nu se poate lega de un camp ramane in randul de stare. */
  arataErorile(erori) {
    const razlete = [];
    for (const eroare of erori) {
      const intrare = this.campulDupa(eroare?.camp);
      if (intrare) this.noteaza(intrare.nod, mesajul(eroare), true);
      else razlete.push(mesajul(eroare));
    }
    this.spuneStarea(razlete.filter(Boolean).join('. '));
    erori.map((e) => this.campulDupa(e?.camp)).find(Boolean)?.control.focus();
  }

  /**
   * Inputurile ascunse care pleaca in cos.
   *
   * Numele lor le decide serverul (§2), nu formularul: „Gravură față", „Font", „_gravura". Se
   * scrie unul singur pe nume, ca sa nu ramana doua controale la fel dupa o re-randare — Shopify
   * ar lua unul din ele si n-am sti care.
   */
  scrieProprietati(proprietati) {
    for (const [nume, nod] of [...this.ascunse]) {
      if (!(nume in proprietati)) { nod.remove(); this.ascunse.delete(nume); }
    }
    for (const [nume, valoare] of Object.entries(proprietati)) {
      let nod = this.ascunse.get(nume);
      if (!nod) {
        nod = document.createElement('input');
        nod.type = 'hidden';
        nod.name = `properties[${nume}]`;
        this.append(nod);
        this.ascunse.set(nume, nod);
      }
      nod.value = valoare ?? '';
    }
  }

  /**
   * Cutia, ca linie separata in cos.
   *
   * Se adauga INAINTE de bijuterie: adaugarea temei reimprospateaza cosul la sfarsit, deci ambele
   * linii apar deodata. Invers, cutia ar sta nevazuta pana la urmatoarea deschidere a cosului.
   */
  /**
   * Poza cutiei, mare.
   *
   * Serverul cere pozele deja micsorate la 240px — cat ii trebuie miniaturii,
   * si cu grija pentru pagina de produs, care are destule imagini. Pentru
   * vitrina se cere aceeasi poza mai mare, rescriind parametrii din adresa CDN.
   * Daca adresa nu are parametrii asteptati, ramane cea mica: o poza mica e mai
   * buna decat una care nu se incarca.
   */
  pozaMare(adresa) {
    if (!adresa) return '';
    try {
      const u = new URL(adresa, window.location.origin);

      // Shopify pune dimensiunea in NUMELE fisierului, nu in parametri:
      // `...poza_240x240.jpg?v=1741874290`. Masurat pe adresele reale ale
      // magazinului — o versiune care rescria `?width=` nu schimba nimic si
      // vitrina ramanea cu miniatura intinsa.
      const marit = u.pathname.replace(/_\d+x\d*(?=\.[a-z]+$)/i, '_1200x');
      if (marit !== u.pathname) { u.pathname = marit; return u.toString(); }

      // Adresele fara sufix accepta parametrul.
      u.searchParams.set('width', '1200');
      u.searchParams.delete('height');
      return u.toString();
    } catch { return adresa; }
  }

  /** Vitrina: cutia, cat sa se vada ce contine. */
  deschideVitrina(cutie) {
    const vitrina = this.querySelector('[data-rol="vitrina"]');
    if (!vitrina?.showModal) return;

    const poza = vitrina.querySelector('[data-rol="vitrina-poza"]');
    poza.src = this.pozaMare(cutie.poza);
    poza.alt = cutie.pozaAlt ?? cutie.eticheta;

    const pret = this.pretulCutiei(cutie);
    vitrina.querySelector('[data-rol="vitrina-nume"]').textContent =
      pret ? `${cutie.eticheta} · ${pret}` : cutie.eticheta;

    // Click pe fundal = inchidere. Un `<dialog>` primeste clickul si cand se
    // apasa langa el, iar tinta e chiar dialogul: continutul e in copii, deci
    // orice click PE dialog inseamna „langa poza". Se leaga o singura data.
    if (!vitrina.dataset.legat) {
      vitrina.addEventListener('click', (ev) => {
        if (ev.target === vitrina) vitrina.close();
      }, { signal: this.semnal });

      // Inchiderea o face JS, nu un <form method="dialog">: snippetul sta in
      // formularul de produs, iar un formular imbricat e aruncat de parser —
      // butonul lui ar ramane al formularului de produs si ar adauga in cos.
      for (const buton of vitrina.querySelectorAll('[data-rol="vitrina-inchide"]')) {
        buton.addEventListener('click', (ev) => {
          ev.preventDefault();
          vitrina.close();
        }, { signal: this.semnal });
      }

      vitrina.dataset.legat = '1';
    }

    vitrina.showModal();
  }

  /**
   * Preturile variantelor, in bani, asa cum le-a scris Liquid.
   *
   * Formatul e `id:pret,id:pret`, nu JSON. Nu din gust: un `{` intr-un filtru
   * Liquid se citeste ca inceputul altei expresii, iar Shopify refuza fisierul
   * cu „Variable was not properly terminated". Perechile n-au acolade.
   */
  preturile() {
    if (this._preturi !== undefined) return this._preturi;
    const harta = {};
    for (const bucata of String(this.dataset.preturi ?? '').split(',')) {
      const taiere = bucata.indexOf(':');
      if (taiere < 1) continue;
      const pret = Number(bucata.slice(taiere + 1));
      if (Number.isFinite(pret)) harta[bucata.slice(0, taiere).trim()] = pret;
    }
    this._preturi = harta;
    return harta;
  }

  /**
   * Totalul de sub cutii: bijuteria plus cutia cadou.
   *
   * Nu inlocuieste pretul temei si nu se atinge de el — acela e pretul piesei,
   * si asa trebuie sa ramana. Linia asta raspunde la intrebarea pe care si-o
   * pune cineva care tocmai a bifat o cutie de 300: „deci cat platesc?".
   *
   * Apare DOAR cand exista o cutie cu pret. Fara ea, „Total: 800" sub un pret
   * de 800 e zgomot.
   */
  actualizeazaTotalul() {
    if (!this.totalNod) return;
    const scris = this.totalCuCutia();
    if (!scris) {
      this.totalNod.hidden = true;
      this.totalNod.textContent = '';
      return;
    }
    this.totalNod.textContent = this.vorba('totalCu', {
      total: scris, cutie: this.impachetareaAleasa()?.eticheta ?? '',
    });
    this.totalNod.hidden = false;
  }

  /**
   * Bijuteria plus cutia cadou, scris in moneda paginii — sau `null` cand nu
   * e nimic de adunat (fara cutie cu pret, ori pretul piesei nu se stie).
   */
  totalCuCutia() {
    const cutie = this.impachetareaAleasa();
    const bijuteria = Number(this.preturile()[String(this.varianta() ?? '')]);
    if (!cutie || cutie.pret <= 0 || !Number.isFinite(bijuteria)) return null;

    // Cifrele se aduna in bani. Pretul cutiei vine ca zecimal din configuratie
    // (250.00), pretul piesei vine deja in subunitati din Liquid (80000).
    // Mesajul de pe cutie intra in total doar cand se factureaza ca linie.
    const detaliu = cutie.detaliu?.mod === 'linie' ? Math.round((cutie.detaliu.pret || 0) * 100) : 0;
    const total = bijuteria + Math.round(cutie.pret * 100) + detaliu;
    return this.baniFrumosi(total, cutie.moneda) || null;
  }

  /**
   * Banii, in moneda paginii. Intra subunitati, iese scris.
   *
   * `ceruta` bate atributul: cand se aduna un total cu o cutie, moneda corecta e
   * cea a cutiei — pe ea a verificat deja `pretulCutiei` ca se potriveste cu
   * pagina. Fara ea, o pagina fara `data-moneda` (bancul, un sablon nou) intorcea
   * sir gol si totalul disparea in tacere, desi cifrele erau bune.
   */
  baniFrumosi(subunitati, ceruta = null) {
    const moneda = ceruta || this.dataset.moneda || window.Shopify?.currency?.active || '';
    if (!moneda) return '';
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'ro', {
        style: 'currency', currency: moneda, maximumFractionDigits: 0,
      }).format(subunitati / 100);
    } catch { return `${Math.round(subunitati / 100)} ${moneda}`; }
  }

  /**
   * Cutia aleasa, pentru cine are nevoie de ea in afara formularului.
   *
   * Comanda ramburs NU trece prin cos: `cod.js` citeste formularul de produs si
   * isi construieste singur liniile. Fara metoda asta, cutia ramanea o simpla
   * proprietate de text pe piesa — clientul o alegea, atelierul o impacheta, si
   * nimeni nu o factura. O „Premium Negru +" de 300 pleca gratis la fiecare
   * comanda ramburs.
   *
   * Se intoarce si pretul numeric, ca totalul sa se poata aduna fara inca o
   * interogare.
   *
   * @returns {{variant_id: string, numar: number, eticheta: string, pret: number, moneda: string|null}|null}
   */
  impachetareaAleasa() {
    const aleasa = (this.config?.impachetari ?? []).find((c) => c.eticheta === this.stare.impachetare);
    if (!aleasa?.variant_id) return null;

    const numar = Number(String(aleasa.variant_id).split('/').pop());
    if (!Number.isFinite(numar)) return null;

    return {
      variant_id: String(aleasa.variant_id),
      numar,
      eticheta: aleasa.eticheta,
      pret: Number.isFinite(aleasa.pret) ? aleasa.pret : 0,
      moneda: aleasa.moneda ?? null,
      detaliu: this.detaliulAles(),
    };
  }

  /**
   * Alegerea de pe cutie, daca exista: mesajul si — cand intra ca linie in
   * comanda — varianta lui, cu pretul. In modul „proprietate" nu se factureaza
   * nimic: ajunge doar ca text pe cutie.
   */
  detaliulAles() {
    const cutie = this.cutiaAleasa();
    const optiune = (cutie?.detaliu?.optiuni ?? []).find((o) => o.eticheta === this.stare.impachetareDetaliu);
    if (!optiune) return null;
    const numar = Number(String(optiune.variant_id ?? '').split('/').pop());
    return {
      eticheta: optiune.eticheta,
      mod: cutie.detaliu.mod,
      variant_id: optiune.variant_id ?? null,
      numar: Number.isFinite(numar) ? numar : null,
      pret: Number.isFinite(optiune.pret) ? optiune.pret : 0,
      moneda: optiune.moneda ?? null,
    };
  }

  async adaugaImpachetarea() {
    const aleasa = (this.config?.impachetari ?? []).find((c) => c.eticheta === this.stare.impachetare);
    if (!aleasa?.variant_id) return;
    // `variant_id` vine ca gid (`gid://shopify/ProductVariant/44123`), iar Ajax Cart API cere
    // numarul. Un `Number(gid)` da NaN si cosul raspunde cu o eroare care nu spune de ce.
    const numarul = Number(String(aleasa.variant_id).split('/').pop());
    if (!Number.isFinite(numarul)) return;
    const items = [{ id: numarul, quantity: 1 }];
    // Mesajul de pe cutie, cand se factureaza separat: inca o linie in cos,
    // cu proprietatea care spune de ce e acolo.
    const detaliu = this.detaliulAles();
    if (detaliu?.mod === 'linie' && detaliu.numar) {
      items.push({ id: detaliu.numar, quantity: 1, properties: { 'Pentru cutia': aleasa.eticheta } });
    }
    try {
      const raspuns = await fetch(`${radacina()}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ items }),
      });
      if (!raspuns.ok) throw new Error(String(raspuns.status));
    } catch {
      this.spuneStarea(this.vorba('eroareImpachetare'));
    }
  }

  /**
   * Ghidul audio: „asculta cum se personalizeaza".
   *
   * Sunetul sta in bucati — una per sectiune (text, simbol, font, impachetare,
   * macheta, final) — iar cand o bucata canta, sectiunea ei se aprinde si vine
   * in vedere. Fara fisiere (inca negenerate), butonul dispare singur: un
   * buton care nu face nimic e mai rau decat lipsa lui.
   */
  pornesteGhidul() {
    const buton = this.querySelector('[data-rol="ghid"]');
    if (!buton) return;
    // Pasul 5 („vreau sa vad macheta") a iesit odata cu bifa lui: clipul se
    // sare, iar numerotarea fisierelor ramane cea de pe server (`FISIERE_GHID`).
    const PASI = ['text', 'simbol', 'font', 'impachetare', 'final'];

    // Exista sunetul? O singura intrebare, la primul fisier.
    fetch(this.adresa('ghid/1.mp3'), { method: 'HEAD', signal: this.semnal })
      .then((raspuns) => { if (raspuns.ok) buton.hidden = false; })
      .catch(() => {});

    buton.addEventListener('click', () => {
      if (this.ghid) { this.opresteGhidul(); return; }
      buton.classList.add('gravura__ghid--canta');
      this.ghid = { pas: 0, sunet: new Audio() };
      this.ghid.sunet.addEventListener('ended', () => this.pasulGhidului(PASI), { signal: this.semnal });
      this.ghid.sunet.addEventListener('error', () => this.opresteGhidul(), { signal: this.semnal });
      this.pasulGhidului(PASI, 0);
    }, { signal: this.semnal });
  }

  pasulGhidului(pasi, deLa = null) {
    if (!this.ghid) return;
    const pas = deLa ?? this.ghid.pas + 1;
    if (pas >= pasi.length) { this.opresteGhidul(); return; }
    this.ghid.pas = pas;

    this.querySelectorAll('.gravura__ghid-aprins').forEach((nod) => nod.classList.remove('gravura__ghid-aprins'));
    const cheie = pasi[pas];
    const tinta = cheie === 'simbol'
      ? this.querySelector('[data-rol="simboluri"]')?.closest('.gravura__camp')
      : cheie === 'final'
        ? this.formular?.querySelector('[type="submit"]')?.closest('div') ?? null
        : this.querySelector(`[data-ghid="${cheie}"]`) ?? (cheie === 'text' ? this.querySelector('[data-ghid="poza"]') : null);
    if (tinta) {
      tinta.classList.add('gravura__ghid-aprins');
      tinta.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }

    this.ghid.sunet.src = this.adresa(`ghid/${FISIERE_GHID[cheie] ?? pas + 1}.mp3`);
    this.ghid.sunet.play().catch(() => this.opresteGhidul());
  }

  opresteGhidul() {
    if (this.ghid) { this.ghid.sunet.pause(); this.ghid = null; }
    this.querySelectorAll('.gravura__ghid-aprins').forEach((nod) => nod.classList.remove('gravura__ghid-aprins'));
    this.querySelector('[data-rol="ghid"]')?.classList.remove('gravura__ghid--canta');
  }

  noteaza(nod, text, rau = false) {
    const nota = nod.querySelector('[data-rol="nota"]');
    nota.textContent = text && rau ? `${this.vorba('deCorectat')}: ${text}` : text;
    nota.classList.toggle('gravura__nota--rau', Boolean(text) && rau);
  }

  spuneStarea(text) {
    const nod = this.stareaGenerala ?? this.querySelector('[data-rol="stare"]');
    if (nod) { nod.textContent = text; nod.hidden = !text; }
  }
}

if (!window.customElements.get('gravura-form')) {
  window.customElements.define('gravura-form', FormularGravura);
}
